/**
 * Template gallery: ready-made, editable banner examples. Each entry is a layout builder
 * (see layouts.ts — all built with stacks) plus a palette, an illustration and texts in
 * English, Ukrainian, Polish, Romanian, Spanish and Russian.
 */
import type { Fill, TemplateDoc } from '../template';
import type { LocaleId } from '../locales';
import type { GalleryImageId, IllustrationId } from './illustrations';
import { LAYOUTS, type Copy, type LayoutId, type Palette } from './layouts';

export * from './illustrations';
export * from './photo-layouts';
export * from './photos';
export type { Copy, Palette } from './layouts';

export type GalleryLocale = LocaleId;
type T = Record<GalleryLocale, string>;
type LocalCopy = {
  [K in keyof Copy]?: Copy[K] extends string[] | undefined ? Record<GalleryLocale, string[]> : T;
};

export const GALLERY_CATEGORIES = [
  'sale',
  'ecommerce',
  'event',
  'social',
  'food',
  'realestate',
  'education',
  'hiring',
  'travel',
  'fitness',
  'holiday',
  'ads',
  'sport',
  'beauty',
  'auto',
  'tech',
  'pets',
] as const;
export type GalleryCategory = (typeof GALLERY_CATEGORIES)[number];

export interface GalleryEntry {
  id: string;
  category: GalleryCategory;
  layout: LayoutId;
  width: number;
  height: number;
  name: T;
  image?: GalleryImageId;
  /** Several pictures (sports: scene + two crests). */
  images?: IllustrationId[];
  /** Photo layouts: Unsplash search queries (English). */
  photo?: string;
  photo2?: string;
  palette: Palette;
  copy: LocalCopy;
}

const lin = (a: string, b: string, angle = 135): Fill => ({
  type: 'linear',
  angle,
  stops: [
    { offset: 0, color: a },
    { offset: 1, color: b },
  ],
});
const sol = (color: string): Fill => ({ type: 'solid', color });
const t = (en: string, uk: string, ru: string, pl: string, ro: string, es: string): T => ({
  en,
  uk,
  ru,
  pl,
  ro,
  es,
});

export const PALETTES = {
  indigo: {
    background: lin('#1E1B4B', '#4338CA'),
    title: '#FFFFFF',
    text: '#C7D2FE',
    accent: '#FACC15',
    accentText: '#111827',
    titleFont: 'Montserrat',
    bodyFont: 'Inter',
  },
  red: {
    background: lin('#7F1D1D', '#DC2626'),
    title: '#FFFFFF',
    text: '#FECACA',
    accent: '#FDE047',
    accentText: '#7F1D1D',
    titleFont: 'Unbounded',
    bodyFont: 'Manrope',
  },
  noir: {
    background: sol('#0A0A0A'),
    title: '#FFFFFF',
    text: '#A3A3A3',
    accent: '#FACC15',
    accentText: '#0A0A0A',
    titleFont: 'Russo One',
    bodyFont: 'Inter',
  },
  cream: {
    background: sol('#FFF7ED'),
    title: '#1C1917',
    text: '#57534E',
    accent: '#EA580C',
    accentText: '#FFFFFF',
    titleFont: 'Playfair Display',
    bodyFont: 'Manrope',
  },
  mint: {
    background: lin('#ECFDF5', '#D1FAE5'),
    title: '#064E3B',
    text: '#047857',
    accent: '#059669',
    accentText: '#FFFFFF',
    titleFont: 'Rubik',
    bodyFont: 'Rubik',
  },
  violet: {
    background: lin('#2E1065', '#7C3AED'),
    title: '#FFFFFF',
    text: '#DDD6FE',
    accent: '#F472B6',
    accentText: '#FFFFFF',
    titleFont: 'Unbounded',
    bodyFont: 'Manrope',
  },
  sky: {
    background: lin('#E0F2FE', '#BAE6FD'),
    title: '#0C4A6E',
    text: '#0369A1',
    accent: '#0284C7',
    accentText: '#FFFFFF',
    titleFont: 'Montserrat',
    bodyFont: 'Inter',
  },
  white: {
    background: sol('#FFFFFF'),
    title: '#111827',
    text: '#4B5563',
    accent: '#4F46E5',
    accentText: '#FFFFFF',
    titleFont: 'Manrope',
    bodyFont: 'Inter',
  },
  forest: {
    background: lin('#052E16', '#166534'),
    title: '#FFFFFF',
    text: '#BBF7D0',
    accent: '#FDE047',
    accentText: '#052E16',
    titleFont: 'Oswald',
    bodyFont: 'Inter',
  },
  sunset: {
    background: lin('#F97316', '#DB2777'),
    title: '#FFFFFF',
    text: '#FFE4E6',
    accent: '#FFFFFF',
    accentText: '#BE185D',
    titleFont: 'Montserrat',
    bodyFont: 'Manrope',
  },
  coffee: {
    background: sol('#292524'),
    title: '#FAFAF9',
    text: '#D6D3D1',
    accent: '#D97706',
    accentText: '#FFFFFF',
    titleFont: 'Playfair Display',
    bodyFont: 'Lora',
  },
  stadium: {
    background: sol('#020617'),
    title: '#FFFFFF',
    text: '#E2E8F0',
    accent: '#A3E635',
    accentText: '#0A0A0A',
    titleFont: 'Unbounded',
    bodyFont: 'Manrope',
  },
  arena: {
    background: sol('#0C0A09'),
    title: '#FFFFFF',
    text: '#E7E5E4',
    accent: '#F97316',
    accentText: '#FFFFFF',
    titleFont: 'Oswald',
    bodyFont: 'Inter',
  },
  stage: {
    background: lin('#1E0B3B', '#4C1D95'),
    title: '#FFFFFF',
    text: '#F5D0FE',
    accent: '#F0ABFC',
    accentText: '#2E1065',
    titleFont: 'Unbounded',
    bodyFont: 'Manrope',
  },
  poster: {
    background: sol('#0A0A0A'),
    title: '#FFFFFF',
    text: '#E5E5E5',
    accent: '#FACC15',
    accentText: '#0A0A0A',
    titleFont: 'Oswald',
    bodyFont: 'Manrope',
  },
  wideTrack: {
    background: sol('#0A0A0A'),
    title: '#FFFFFF',
    text: '#FFFFFF',
    accent: '#F97316',
    accentText: '#FFFFFF',
    titleFont: 'Tenor Sans',
    bodyFont: 'Manrope',
  },
  paper: {
    background: sol('#F3EEE6'),
    title: '#1C1917',
    text: '#57534E',
    accent: '#9A3412',
    accentText: '#FFFFFF',
    titleFont: 'Cormorant Garamond',
    bodyFont: 'Manrope',
  },
  sage: {
    background: sol('#E7EBE3'),
    title: '#1F2A1F',
    text: '#4B5A4B',
    accent: '#3F6212',
    accentText: '#FFFFFF',
    titleFont: 'Playfair Display',
    bodyFont: 'Inter',
  },
  script: {
    background: sol('#1C1917'),
    title: '#FFFFFF',
    text: '#F5F5F4',
    accent: '#FDE68A',
    accentText: '#1C1917',
    titleFont: 'Cormorant Garamond',
    bodyFont: 'Manrope',
  },
  punch: {
    background: sol('#0A0A0A'),
    title: '#111111',
    text: '#FFFFFF',
    accent: '#FACC15',
    accentText: '#111111',
    titleFont: 'Rubik',
    bodyFont: 'Rubik',
  },
  ocean: {
    background: lin('#0F172A', '#0E7490'),
    title: '#FFFFFF',
    text: '#A5F3FC',
    accent: '#22D3EE',
    accentText: '#0F172A',
    titleFont: 'Exo 2',
    bodyFont: 'Inter',
  },
  rose: {
    background: lin('#FFF1F2', '#FFE4E6'),
    title: '#881337',
    text: '#9F1239',
    accent: '#E11D48',
    accentText: '#FFFFFF',
    titleFont: 'Playfair Display',
    bodyFont: 'Manrope',
  },
  gold: {
    background: sol('#111111'),
    title: '#FDF6E3',
    text: '#D6CFC0',
    accent: '#D4A537',
    accentText: '#111111',
    titleFont: 'Playfair Display',
    bodyFont: 'Inter',
  },
  sunny: {
    background: sol('#FDE047'),
    title: '#111827',
    text: '#374151',
    accent: '#111827',
    accentText: '#FDE047',
    titleFont: 'Unbounded',
    bodyFont: 'Manrope',
  },
} satisfies Record<string, Palette>;
export type PaletteId = keyof typeof PALETTES;
const P = PALETTES;
/** Lines given per line in every language → per language, a list of lines. */
const rows = (lines: T[]): Record<GalleryLocale, string[]> =>
  Object.fromEntries(
    (Object.keys(lines[0]) as GalleryLocale[]).map((l) => [l, lines.map((x) => x[l])]),
  ) as Record<GalleryLocale, string[]>;

const SHOP = t(
  'Shop now',
  'Купити зараз',
  'Купить сейчас',
  'Kup teraz',
  'Cumpără acum',
  'Compra ahora',
);
const MORE = t(
  'Learn more',
  'Детальніше',
  'Подробнее',
  'Dowiedz się więcej',
  'Află mai multe',
  'Más información',
);
const BOOK = t(
  'Book now',
  'Забронювати',
  'Забронировать',
  'Zarezerwuj',
  'Rezervă acum',
  'Reserva ahora',
);
const JOIN = t(
  'Register',
  'Зареєструватися',
  'Зарегистрироваться',
  'Zarejestruj się',
  'Înregistrează-te',
  'Regístrate',
);

export const GALLERY: GalleryEntry[] = [
  // ---------------------------------------------------------------- sale
  {
    id: 'summer-sale',
    category: 'sale',
    layout: 'split',
    width: 1080,
    height: 1080,
    image: 'fashion-selfie',
    palette: P.indigo,
    name: t(
      'Summer sale',
      'Літній розпродаж',
      'Летняя распродажа',
      'Letnia wyprzedaż',
      'Reduceri de vară',
      'Rebajas de verano',
    ),
    copy: {
      kicker: t(
        'Summer collection',
        'Літня колекція',
        'Летняя коллекция',
        'Kolekcja letnia',
        'Colecția de vară',
        'Colección de verano',
      ),
      title: t(
        'Summer sale',
        'Літній розпродаж',
        'Летняя распродажа',
        'Letnia wyprzedaż',
        'Reduceri de vară',
        'Rebajas de verano',
      ),
      subtitle: t(
        'Discounts on the whole collection until Sunday',
        'Знижки на всю колекцію до неділі',
        'Скидки на всю коллекцию до воскресенья',
        'Rabaty na całą kolekcję do niedzieli',
        'Reduceri la toată colecția până duminică',
        'Descuentos en toda la colección hasta el domingo',
      ),
      badge: t('-30%', '-30%', '-30%', '-30%', '-30%', '-30%'),
      price: t('$29.90', '$29.90', '$29.90', '$29.90', '$29.90', '$29.90'),
    },
  },
  {
    id: 'flash-sale',
    category: 'sale',
    layout: 'number',
    width: 1080,
    height: 1080,
    palette: P.red,
    name: t(
      'Flash sale',
      'Флеш-розпродаж',
      'Флеш-распродажа',
      'Wyprzedaż flash',
      'Reduceri fulger',
      'Venta flash',
    ),
    copy: {
      kicker: t(
        'Today only',
        'Лише сьогодні',
        'Только сегодня',
        'Tylko dziś',
        'Doar azi',
        'Solo hoy',
      ),
      badge: t('-50%', '-50%', '-50%', '-50%', '-50%', '-50%'),
      title: t(
        'Flash sale on everything',
        'Флеш-розпродаж на все',
        'Флеш-распродажа на всё',
        'Wyprzedaż flash na wszystko',
        'Reduceri fulger la tot',
        'Venta flash en todo',
      ),
      subtitle: t(
        'Ends at midnight. No promo code needed.',
        'До опівночі. Без промокоду.',
        'До полуночи. Без промокода.',
        'Do północy. Bez kodu rabatowego.',
        'Până la miezul nopții. Fără cod promo.',
        'Hasta medianoche. Sin código promocional.',
      ),
      cta: SHOP,
    },
  },
  {
    id: 'black-friday-story',
    category: 'sale',
    layout: 'number',
    width: 1080,
    height: 1920,
    palette: P.noir,
    name: t(
      'Black Friday story',
      'Black Friday — сторіс',
      'Black Friday — сторис',
      'Black Friday — relacja',
      'Black Friday — story',
      'Black Friday — historia',
    ),
    copy: {
      kicker: t(
        'Black Friday',
        'Black Friday',
        'Black Friday',
        'Black Friday',
        'Black Friday',
        'Black Friday',
      ),
      badge: t('-70%', '-70%', '-70%', '-70%', '-70%', '-70%'),
      title: t(
        'The biggest sale of the year',
        'Найбільший розпродаж року',
        'Самая большая распродажа года',
        'Największa wyprzedaż roku',
        'Cele mai mari reduceri ale anului',
        'Las mayores rebajas del año',
      ),
      subtitle: t(
        'Only 3 days. Thousands of products.',
        'Лише 3 дні. Тисячі товарів.',
        'Только 3 дня. Тысячи товаров.',
        'Tylko 3 dni. Tysiące produktów.',
        'Doar 3 zile. Mii de produse.',
        'Solo 3 días. Miles de productos.',
      ),
      cta: SHOP,
    },
  },
  {
    id: 'sale-story',
    category: 'sale',
    layout: 'hero',
    width: 1080,
    height: 1920,
    image: 'sneaker',
    palette: P.sunset,
    name: t(
      'Sale story with photo',
      'Сторіс розпродажу з фото',
      'Сторис распродажи с фото',
      'Relacja wyprzedaży ze zdjęciem',
      'Story de reduceri cu foto',
      'Historia de rebajas con foto',
    ),
    copy: {
      kicker: t(
        'New season',
        'Новий сезон',
        'Новый сезон',
        'Nowy sezon',
        'Sezon nou',
        'Nueva temporada',
      ),
      title: t(
        'Up to 40% off sneakers',
        'До −40% на кросівки',
        'До −40% на кроссовки',
        'Sneakersy do −40%',
        'Sneakers cu până la −40%',
        'Hasta −40% en zapatillas',
      ),
      subtitle: t(
        'Free delivery on orders over $50',
        'Безкоштовна доставка від $50',
        'Бесплатная доставка от $50',
        'Darmowa dostawa od $50',
        'Livrare gratuită de la $50',
        'Envío gratis desde $50',
      ),
      cta: SHOP,
    },
  },
  {
    id: 'sale-landscape',
    category: 'sale',
    layout: 'split',
    width: 1200,
    height: 628,
    image: 'sneaker',
    palette: P.violet,
    name: t(
      'Sale — link preview',
      'Розпродаж — прев’ю посилання',
      'Распродажа — превью ссылки',
      'Wyprzedaż — podgląd linku',
      'Reduceri — previzualizare link',
      'Rebajas — vista previa de enlace',
    ),
    copy: {
      kicker: t(
        'Weekend deal',
        'Пропозиція вихідних',
        'Предложение выходных',
        'Oferta weekendowa',
        'Ofertă de weekend',
        'Oferta de fin de semana',
      ),
      title: t(
        'Sneakers from $29',
        'Кросівки від $29',
        'Кроссовки от $29',
        'Sneakersy od $29',
        'Sneakers de la $29',
        'Zapatillas desde $29',
      ),
      subtitle: t(
        'Hundreds of models in stock',
        'Сотні моделей у наявності',
        'Сотни моделей в наличии',
        'Setki modeli na stanie',
        'Sute de modele în stoc',
        'Cientos de modelos en stock',
      ),
      cta: SHOP,
    },
  },
  // ---------------------------------------------------------------- e-commerce
  {
    id: 'new-collection',
    category: 'ecommerce',
    layout: 'card',
    width: 1080,
    height: 1350,
    image: 'sneaker',
    palette: P.white,
    name: t(
      'New arrival card',
      'Картка новинки',
      'Карточка новинки',
      'Karta nowości',
      'Card noutate',
      'Tarjeta de novedad',
    ),
    copy: {
      kicker: t('New arrival', 'Новинка', 'Новинка', 'Nowość', 'Noutate', 'Novedad'),
      title: t(
        'Air Runner 2',
        'Air Runner 2',
        'Air Runner 2',
        'Air Runner 2',
        'Air Runner 2',
        'Air Runner 2',
      ),
      subtitle: t(
        'Light, fast and breathable',
        'Легкі, швидкі, дихаючі',
        'Лёгкие, быстрые, дышащие',
        'Lekkie, szybkie, oddychające',
        'Ușori, rapizi și respirabili',
        'Ligeras, rápidas y transpirables',
      ),
      price: t('$89', '$89', '$89', '$89', '$89', '$89'),
      cta: SHOP,
    },
  },
  {
    id: 'headphones-promo',
    category: 'ecommerce',
    layout: 'split',
    width: 1200,
    height: 628,
    image: 'headphones',
    palette: P.violet,
    name: t(
      'Product promo',
      'Промо товару',
      'Промо товара',
      'Promocja produktu',
      'Promo produs',
      'Promo de producto',
    ),
    copy: {
      kicker: t(
        'Wireless',
        'Бездротові',
        'Беспроводные',
        'Bezprzewodowe',
        'Fără fir',
        'Inalámbricos',
      ),
      title: t(
        'Sound without limits',
        'Звук без обмежень',
        'Звук без ограничений',
        'Dźwięk bez ograniczeń',
        'Sunet fără limite',
        'Sonido sin límites',
      ),
      subtitle: t(
        '40 hours of music on one charge',
        '40 годин музики на одному заряді',
        '40 часов музыки на одном заряде',
        '40 godzin muzyki na jednym ładowaniu',
        '40 de ore de muzică la o încărcare',
        '40 horas de música con una carga',
      ),
      badge: t('-20%', '-20%', '-20%', '-20%', '-20%', '-20%'),
      price: t('$129', '$129', '$129', '$129', '$129', '$129'),
    },
  },
  {
    id: 'headphones-story',
    category: 'ecommerce',
    layout: 'card',
    width: 1080,
    height: 1920,
    image: 'headphones',
    palette: P.violet,
    name: t(
      'Product story',
      'Сторіс товару',
      'Сторис товара',
      'Relacja produktu',
      'Story produs',
      'Historia de producto',
    ),
    copy: {
      kicker: t(
        'Best seller',
        'Хіт продажів',
        'Хит продаж',
        'Bestseller',
        'Cel mai vândut',
        'Más vendido',
      ),
      title: t(
        'Studio Pro headphones',
        'Навушники Studio Pro',
        'Наушники Studio Pro',
        'Słuchawki Studio Pro',
        'Căști Studio Pro',
        'Auriculares Studio Pro',
      ),
      subtitle: t(
        'Noise cancelling and 40 hours of battery',
        'Шумозаглушення та 40 годин роботи',
        'Шумоподавление и 40 часов работы',
        'Redukcja szumów i 40 godzin pracy',
        'Anulare zgomot și 40 de ore autonomie',
        'Cancelación de ruido y 40 horas de batería',
      ),
      price: t('$129', '$129', '$129', '$129', '$129', '$129'),
      cta: SHOP,
    },
  },
  {
    id: 'product-portrait',
    category: 'ecommerce',
    layout: 'split',
    width: 1080,
    height: 1350,
    image: 'headphones',
    palette: P.sky,
    name: t(
      'Product post 4:5',
      'Пост товару 4:5',
      'Пост товара 4:5',
      'Post produktu 4:5',
      'Postare produs 4:5',
      'Publicación de producto 4:5',
    ),
    copy: {
      kicker: t(
        'Limited edition',
        'Лімітована серія',
        'Лимитированная серия',
        'Edycja limitowana',
        'Ediție limitată',
        'Edición limitada',
      ),
      title: t(
        'Colors of summer',
        'Кольори літа',
        'Цвета лета',
        'Kolory lata',
        'Culorile verii',
        'Colores del verano',
      ),
      subtitle: t(
        'Pick yours from 6 colors',
        'Оберіть свій із 6 кольорів',
        'Выберите свой из 6 цветов',
        'Wybierz swój z 6 kolorów',
        'Alege-ți una din 6 culori',
        'Elige el tuyo entre 6 colores',
      ),
      price: t('$99', '$99', '$99', '$99', '$99', '$99'),
      badge: t('NEW', 'NEW', 'NEW', 'NEW', 'NEW', 'NEW'),
    },
  },
  // ---------------------------------------------------------------- events
  {
    id: 'webinar',
    category: 'event',
    layout: 'event',
    width: 1080,
    height: 1080,
    image: 'laptop',
    palette: P.white,
    name: t('Webinar', 'Вебінар', 'Вебинар', 'Webinar', 'Webinar', 'Webinar'),
    copy: {
      kicker: t(
        'MAY 24 · 18:00',
        '24 ТРАВНЯ · 18:00',
        '24 МАЯ · 18:00',
        '24 MAJA · 18:00',
        '24 MAI · 18:00',
        '24 DE MAYO · 18:00',
      ),
      title: t(
        'How to automate creatives',
        'Як автоматизувати креативи',
        'Как автоматизировать креативы',
        'Jak zautomatyzować kreacje',
        'Cum să automatizezi creativele',
        'Cómo automatizar creatividades',
      ),
      details: {
        en: ['Free online webinar', 'Q&A with the team'],
        uk: ['Безкоштовний онлайн-вебінар', 'Відповіді на запитання'],
        ru: ['Бесплатный онлайн-вебинар', 'Ответы на вопросы'],
        pl: ['Bezpłatny webinar online', 'Pytania i odpowiedzi z zespołem'],
        ro: ['Webinar online gratuit', 'Întrebări și răspunsuri cu echipa'],
        es: ['Webinar online gratuito', 'Preguntas y respuestas con el equipo'],
      },
      cta: JOIN,
    },
  },
  {
    id: 'conference',
    category: 'event',
    layout: 'event',
    width: 1200,
    height: 628,
    palette: P.ocean,
    name: t('Conference', 'Конференція', 'Конференция', 'Konferencja', 'Conferință', 'Conferencia'),
    copy: {
      kicker: t(
        'OCT 12–13',
        '12–13 ЖОВТНЯ',
        '12–13 ОКТЯБРЯ',
        '12–13 PAŹDZIERNIKA',
        '12–13 OCTOMBRIE',
        '12–13 DE OCTUBRE',
      ),
      title: t(
        'Marketing Tech Summit',
        'Marketing Tech Summit',
        'Marketing Tech Summit',
        'Marketing Tech Summit',
        'Marketing Tech Summit',
        'Marketing Tech Summit',
      ),
      details: {
        en: ['40 speakers · 2 days', 'Kyiv & online'],
        uk: ['40 спікерів · 2 дні', 'Київ та онлайн'],
        ru: ['40 спикеров · 2 дня', 'Киев и онлайн'],
        pl: ['40 prelegentów · 2 dni', 'Kijów i online'],
        ro: ['40 de speakeri · 2 zile', 'Kiev și online'],
        es: ['40 ponentes · 2 días', 'Kiev y online'],
      },
      cta: t(
        'Get a ticket',
        'Купити квиток',
        'Купить билет',
        'Kup bilet',
        'Cumpără bilet',
        'Compra tu entrada',
      ),
    },
  },
  {
    id: 'meetup-story',
    category: 'event',
    layout: 'event',
    width: 1080,
    height: 1920,
    palette: P.violet,
    name: t(
      'Meetup story',
      'Мітап — сторіс',
      'Митап — сторис',
      'Meetup — relacja',
      'Meetup — story',
      'Meetup — historia',
    ),
    copy: {
      kicker: t(
        'FRI · 19:00',
        'ПТ · 19:00',
        'ПТ · 19:00',
        'PT · 19:00',
        'VIN · 19:00',
        'VIE · 19:00',
      ),
      title: t(
        'Design & coffee meetup',
        'Мітап «Дизайн і кава»',
        'Митап «Дизайн и кофе»',
        'Meetup „Design i kawa”',
        'Meetup „Design și cafea”',
        'Meetup de diseño y café',
      ),
      details: {
        en: ['Talks, networking and pizza', 'Entry is free'],
        uk: ['Доповіді, нетворкінг і піца', 'Вхід вільний'],
        ru: ['Доклады, нетворкинг и пицца', 'Вход свободный'],
        pl: ['Prelekcje, networking i pizza', 'Wstęp wolny'],
        ro: ['Prezentări, networking și pizza', 'Intrarea e liberă'],
        es: ['Charlas, networking y pizza', 'Entrada libre'],
      },
      cta: JOIN,
    },
  },
  // ---------------------------------------------------------------- social
  {
    id: 'quote',
    category: 'social',
    layout: 'quote',
    width: 1080,
    height: 1080,
    palette: P.cream,
    name: t('Quote', 'Цитата', 'Цитата', 'Cytat', 'Citat', 'Cita'),
    copy: {
      title: t(
        'Simplicity is the ultimate sophistication.',
        'Простота — найвища витонченість.',
        'Простота — высшая изысканность.',
        'Prostota jest szczytem wyrafinowania.',
        'Simplitatea este rafinamentul suprem.',
        'La simplicidad es la máxima sofisticación.',
      ),
      author: t(
        'Leonardo da Vinci',
        'Леонардо да Вінчі',
        'Леонардо да Винчи',
        'Leonardo da Vinci',
        'Leonardo da Vinci',
        'Leonardo da Vinci',
      ),
      role: t(
        'Artist and inventor',
        'Художник і винахідник',
        'Художник и изобретатель',
        'Artysta i wynalazca',
        'Artist și inventator',
        'Artista e inventor',
      ),
    },
  },
  {
    id: 'testimonial',
    category: 'social',
    layout: 'quote',
    width: 1080,
    height: 1350,
    palette: P.indigo,
    name: t(
      'Customer review',
      'Відгук клієнта',
      'Отзыв клиента',
      'Opinia klienta',
      'Recenzie client',
      'Opinión de cliente',
    ),
    copy: {
      title: t(
        'We now launch campaigns in a day instead of a week. The team finally has time for ideas.',
        'Тепер ми запускаємо кампанії за день замість тижня. У команди нарешті є час на ідеї.',
        'Теперь мы запускаем кампании за день вместо недели. У команды наконец есть время на идеи.',
        'Teraz uruchamiamy kampanie w jeden dzień zamiast tygodnia. Zespół wreszcie ma czas na pomysły.',
        'Acum lansăm campanii într-o zi în loc de o săptămână. Echipa are în sfârșit timp de idei.',
        'Ahora lanzamos campañas en un día en lugar de una semana. Por fin el equipo tiene tiempo para ideas.',
      ),
      author: t(
        'Anna Koval',
        'Анна Коваль',
        'Анна Коваль',
        'Anna Kowal',
        'Anna Koval',
        'Anna Koval',
      ),
      role: t(
        'Head of marketing',
        'Керівниця маркетингу',
        'Руководитель маркетинга',
        'Szefowa marketingu',
        'Directoare de marketing',
        'Directora de marketing',
      ),
    },
  },
  {
    id: 'announcement',
    category: 'social',
    layout: 'hero',
    width: 1080,
    height: 1080,
    image: 'abstract',
    palette: P.violet,
    name: t('Announcement', 'Анонс', 'Анонс', 'Ogłoszenie', 'Anunț', 'Anuncio'),
    copy: {
      kicker: t(
        'Big news',
        'Важлива новина',
        'Важная новость',
        'Ważna wiadomość',
        'Vești mari',
        'Grandes noticias',
      ),
      title: t(
        'We are launching something new',
        'Ми запускаємо дещо нове',
        'Мы запускаем кое-что новое',
        'Startujemy z czymś nowym',
        'Lansăm ceva nou',
        'Lanzamos algo nuevo',
      ),
      subtitle: t(
        'Stay tuned — details on Monday',
        'Слідкуйте — подробиці в понеділок',
        'Следите — подробности в понедельник',
        'Bądź czujny — szczegóły w poniedziałek',
        'Rămâi aproape — detalii luni',
        'No te lo pierdas — detalles el lunes',
      ),
    },
  },
  {
    id: 'post-portrait',
    category: 'social',
    layout: 'number',
    width: 1080,
    height: 1350,
    palette: P.sky,
    name: t('Milestone', 'Досягнення', 'Достижение', 'Osiągnięcie', 'Realizare', 'Hito'),
    copy: {
      kicker: t('Thank you!', 'Дякуємо!', 'Спасибо!', 'Dziękujemy!', 'Mulțumim!', '¡Gracias!'),
      badge: t('10K', '10K', '10K', '10K', '10K', '10K'),
      title: t(
        'followers in our community',
        'підписників у нашій спільноті',
        'подписчиков в нашем сообществе',
        'obserwujących w naszej społeczności',
        'de urmăritori în comunitatea noastră',
        'seguidores en nuestra comunidad',
      ),
      subtitle: t(
        'Giveaway for everyone this week',
        'Цього тижня — розіграш для всіх',
        'На этой неделе — розыгрыш для всех',
        'W tym tygodniu — konkurs dla wszystkich',
        'Săptămâna asta — giveaway pentru toți',
        'Esta semana — sorteo para todos',
      ),
    },
  },
  // ---------------------------------------------------------------- food
  {
    id: 'restaurant',
    category: 'food',
    layout: 'split',
    width: 1080,
    height: 1080,
    image: 'strawberries',
    palette: P.cream,
    name: t(
      'Restaurant dish',
      'Страва ресторану',
      'Блюдо ресторана',
      'Danie restauracji',
      'Preparat de restaurant',
      'Plato de restaurante',
    ),
    copy: {
      kicker: t(
        'Chef’s special',
        'Від шефа',
        'От шефа',
        'Od szefa kuchni',
        'Recomandarea bucătarului',
        'Sugerencia del chef',
      ),
      title: t(
        'Fresh summer bowl',
        'Свіжий літній боул',
        'Свежий летний боул',
        'Świeży letni bowl',
        'Bowl proaspăt de vară',
        'Bowl fresco de verano',
      ),
      subtitle: t(
        'Quinoa, avocado, tomatoes and lime dressing',
        'Кіноа, авокадо, томати та лаймова заправка',
        'Киноа, авокадо, томаты и лаймовая заправка',
        'Komosa, awokado, pomidory i sos limonkowy',
        'Quinoa, avocado, roșii și dressing de lime',
        'Quinoa, aguacate, tomate y aliño de lima',
      ),
      price: t('$12', '$12', '$12', '$12', '$12', '$12'),
      badge: t('NEW', 'NEW', 'NEW', 'NEW', 'NEW', 'NEW'),
    },
  },
  {
    id: 'coffee',
    category: 'food',
    layout: 'card',
    width: 1080,
    height: 1350,
    image: 'coffee-cup',
    palette: P.coffee,
    name: t('Coffee shop', 'Кав’ярня', 'Кофейня', 'Kawiarnia', 'Cafenea', 'Cafetería'),
    copy: {
      kicker: t(
        'Every morning',
        'Щоранку',
        'Каждое утро',
        'Każdego ranka',
        'În fiecare dimineață',
        'Cada mañana',
      ),
      title: t(
        'Second cup is free',
        'Друга чашка — у подарунок',
        'Вторая чашка — в подарок',
        'Druga kawa gratis',
        'A doua ceașcă e gratis',
        'La segunda taza, gratis',
      ),
      subtitle: t(
        'Until 10:00 at all our coffee shops',
        'До 10:00 у всіх наших кав’ярнях',
        'До 10:00 во всех наших кофейнях',
        'Do 10:00 we wszystkich naszych kawiarniach',
        'Până la 10:00 în toate cafenelele noastre',
        'Hasta las 10:00 en todas nuestras cafeterías',
      ),
      cta: t('Find us', 'Знайти нас', 'Найти нас', 'Znajdź nas', 'Găsește-ne', 'Encuéntranos'),
    },
  },
  {
    id: 'food-delivery',
    category: 'food',
    layout: 'hero',
    width: 1200,
    height: 628,
    image: 'pizza',
    palette: P.sunset,
    name: t(
      'Food delivery',
      'Доставка їжі',
      'Доставка еды',
      'Dostawa jedzenia',
      'Livrare mâncare',
      'Comida a domicilio',
    ),
    copy: {
      kicker: t(
        '30 minutes or free',
        '30 хвилин або безкоштовно',
        '30 минут или бесплатно',
        '30 minut albo za darmo',
        '30 de minute sau gratis',
        '30 minutos o gratis',
      ),
      title: t(
        'Dinner is on its way',
        'Вечеря вже їде',
        'Ужин уже едет',
        'Kolacja już jedzie',
        'Cina e pe drum',
        'La cena va en camino',
      ),
      cta: t('Order now', 'Замовити', 'Заказать', 'Zamów teraz', 'Comandă acum', 'Pide ahora'),
    },
  },
  // ---------------------------------------------------------------- real estate
  {
    id: 'house-sale',
    category: 'realestate',
    layout: 'split',
    width: 1080,
    height: 1080,
    image: 'cozy-house',
    palette: P.forest,
    name: t(
      'House for sale',
      'Будинок на продаж',
      'Дом на продажу',
      'Dom na sprzedaż',
      'Casă de vânzare',
      'Casa en venta',
    ),
    copy: {
      kicker: t('For sale', 'Продається', 'Продаётся', 'Na sprzedaż', 'De vânzare', 'En venta'),
      title: t(
        'Family house with a garden',
        'Сімейний будинок із садом',
        'Семейный дом с садом',
        'Dom rodzinny z ogrodem',
        'Casă de familie cu grădină',
        'Casa familiar con jardín',
      ),
      subtitle: t(
        '4 rooms · 160 m² · 10 min to the center',
        '4 кімнати · 160 м² · 10 хв до центру',
        '4 комнаты · 160 м² · 10 мин до центра',
        '4 pokoje · 160 m² · 10 min do centrum',
        '4 camere · 160 m² · 10 min de centru',
        '4 habitaciones · 160 m² · 10 min del centro',
      ),
      price: t('$245,000', '$245 000', '$245 000', '$245 000', '$245.000', '$245.000'),
    },
  },
  {
    id: 'open-house',
    category: 'realestate',
    layout: 'card',
    width: 1080,
    height: 1920,
    image: 'window-view',
    palette: P.white,
    name: t(
      'Open house story',
      'День відкритих дверей — сторіс',
      'День открытых дверей — сторис',
      'Dzień otwarty — relacja',
      'Ziua porților deschise — story',
      'Puertas abiertas — historia',
    ),
    copy: {
      kicker: t(
        'Open house · Saturday',
        'Відкриті двері · субота',
        'Открытые двери · суббота',
        'Dzień otwarty · sobota',
        'Porți deschise · sâmbătă',
        'Puertas abiertas · sábado',
      ),
      title: t(
        'Come see your new home',
        'Приходьте подивитися новий дім',
        'Приходите посмотреть новый дом',
        'Przyjdź zobaczyć swój nowy dom',
        'Vino să-ți vezi noua casă',
        'Ven a ver tu nuevo hogar',
      ),
      subtitle: t(
        '12:00–16:00, Green street 15',
        '12:00–16:00, вул. Зелена, 15',
        '12:00–16:00, ул. Зелёная, 15',
        '12:00–16:00, ul. Zielona 15',
        '12:00–16:00, str. Verde 15',
        '12:00–16:00, calle Verde 15',
      ),
      cta: BOOK,
    },
  },
  // ---------------------------------------------------------------- education
  {
    id: 'online-course',
    category: 'education',
    layout: 'split',
    width: 1200,
    height: 628,
    image: 'trading-screen',
    palette: P.mint,
    name: t(
      'Online course',
      'Онлайн-курс',
      'Онлайн-курс',
      'Kurs online',
      'Curs online',
      'Curso online',
    ),
    copy: {
      kicker: t(
        'Starts June 1',
        'Старт 1 червня',
        'Старт 1 июня',
        'Start 1 czerwca',
        'Începe pe 1 iunie',
        'Empieza el 1 de junio',
      ),
      title: t(
        'Data analytics from scratch',
        'Аналітика даних з нуля',
        'Аналитика данных с нуля',
        'Analiza danych od zera',
        'Analiza datelor de la zero',
        'Análisis de datos desde cero',
      ),
      subtitle: t(
        '8 weeks · projects for your portfolio',
        '8 тижнів · проєкти для портфоліо',
        '8 недель · проекты для портфолио',
        '8 tygodni · projekty do portfolio',
        '8 săptămâni · proiecte pentru portofoliu',
        '8 semanas · proyectos para tu portafolio',
      ),
      cta: JOIN,
    },
  },
  {
    id: 'course-story',
    category: 'education',
    layout: 'card',
    width: 1080,
    height: 1920,
    image: 'remote-work',
    palette: P.mint,
    name: t(
      'Course story',
      'Сторіс курсу',
      'Сторис курса',
      'Relacja kursu',
      'Story curs',
      'Historia de curso',
    ),
    copy: {
      kicker: t(
        'Free lesson',
        'Безкоштовний урок',
        'Бесплатный урок',
        'Darmowa lekcja',
        'Lecție gratuită',
        'Clase gratuita',
      ),
      title: t(
        'Learn Excel in one evening',
        'Excel за один вечір',
        'Excel за один вечер',
        'Excel w jeden wieczór',
        'Excel într-o singură seară',
        'Excel en una sola tarde',
      ),
      subtitle: t(
        'Formulas, charts and pivot tables',
        'Формули, графіки та зведені таблиці',
        'Формулы, графики и сводные таблицы',
        'Formuły, wykresy i tabele przestawne',
        'Formule, grafice și tabele pivot',
        'Fórmulas, gráficos y tablas dinámicas',
      ),
      cta: JOIN,
    },
  },
  // ---------------------------------------------------------------- hiring
  {
    id: 'we-are-hiring',
    category: 'hiring',
    layout: 'hero',
    width: 1080,
    height: 1080,
    image: 'office-building',
    palette: P.indigo,
    name: t(
      'We are hiring',
      'Ми наймаємо',
      'Мы нанимаем',
      'Rekrutujemy',
      'Angajăm',
      'Estamos contratando',
    ),
    copy: {
      kicker: t(
        'Join the team',
        'Приєднуйтеся до команди',
        'Присоединяйтесь к команде',
        'Dołącz do zespołu',
        'Alătură-te echipei',
        'Únete al equipo',
      ),
      title: t(
        'We are hiring designers',
        'Шукаємо дизайнерів',
        'Ищем дизайнеров',
        'Szukamy projektantów',
        'Căutăm designeri',
        'Buscamos diseñadores',
      ),
      subtitle: t(
        'Remote · full-time · great team',
        'Віддалено · повна зайнятість · класна команда',
        'Удалённо · полная занятость · классная команда',
        'Zdalnie · pełny etat · świetny zespół',
        'Remote · normă întreagă · echipă super',
        'Remoto · jornada completa · gran equipo',
      ),
      cta: t('Apply', 'Відгукнутися', 'Откликнуться', 'Aplikuj', 'Aplică', 'Envía tu CV'),
    },
  },
  {
    id: 'job-post',
    category: 'hiring',
    layout: 'event',
    width: 1200,
    height: 628,
    palette: P.white,
    name: t(
      'Job post',
      'Вакансія',
      'Вакансия',
      'Oferta pracy',
      'Anunț de angajare',
      'Oferta de empleo',
    ),
    copy: {
      kicker: t('VACANCY', 'ВАКАНСІЯ', 'ВАКАНСИЯ', 'OFERTA PRACY', 'POST VACANT', 'VACANTE'),
      title: t(
        'Senior Frontend Developer',
        'Senior Frontend Developer',
        'Senior Frontend Developer',
        'Senior Frontend Developer',
        'Senior Frontend Developer',
        'Senior Frontend Developer',
      ),
      details: {
        en: ['React, TypeScript · remote', 'Salary from $4,000'],
        uk: ['React, TypeScript · віддалено', 'Зарплата від $4 000'],
        ru: ['React, TypeScript · удалённо', 'Зарплата от $4 000'],
        pl: ['React, TypeScript · zdalnie', 'Wynagrodzenie od $4 000'],
        ro: ['React, TypeScript · remote', 'Salariu de la $4.000'],
        es: ['React, TypeScript · remoto', 'Salario desde $4.000'],
      },
      cta: MORE,
    },
  },
  // ---------------------------------------------------------------- travel
  {
    id: 'travel-story',
    category: 'travel',
    layout: 'hero',
    width: 1080,
    height: 1920,
    image: 'mountain-lake',
    palette: P.ocean,
    name: t(
      'Travel deal story',
      'Тур — сторіс',
      'Тур — сторис',
      'Wycieczka — relacja',
      'Ofertă de vacanță — story',
      'Oferta de viaje — historia',
    ),
    copy: {
      kicker: t(
        'Hot tour',
        'Гарячий тур',
        'Горячий тур',
        'Last minute',
        'Last minute',
        'Última hora',
      ),
      title: t(
        '7 days in the mountains',
        '7 днів у горах',
        '7 дней в горах',
        '7 dni w górach',
        '7 zile la munte',
        '7 días en la montaña',
      ),
      subtitle: t(
        'Flights, hotel and guide included — from $590',
        'Переліт, готель і гід — від $590',
        'Перелёт, отель и гид — от $590',
        'Lot, hotel i przewodnik — od $590',
        'Zbor, hotel și ghid incluse — de la $590',
        'Vuelo, hotel y guía incluidos — desde $590',
      ),
      cta: BOOK,
    },
  },
  {
    id: 'travel-banner',
    category: 'travel',
    layout: 'split',
    width: 1200,
    height: 628,
    image: 'beach-umbrella',
    palette: P.sunset,
    name: t(
      'Travel banner',
      'Банер подорожі',
      'Баннер путешествия',
      'Baner podróżniczy',
      'Banner de călătorie',
      'Banner de viaje',
    ),
    copy: {
      kicker: t(
        'Early booking',
        'Раннє бронювання',
        'Раннее бронирование',
        'Wczesna rezerwacja',
        'Rezervare timpurie',
        'Reserva anticipada',
      ),
      title: t(
        'Summer by the sea',
        'Літо біля моря',
        'Лето у моря',
        'Lato nad morzem',
        'Vară la mare',
        'Verano junto al mar',
      ),
      subtitle: t(
        'Save up to 25% when you book now',
        'Заощадьте до 25% при бронюванні зараз',
        'Сэкономьте до 25% при бронировании сейчас',
        'Zaoszczędź do 25%, rezerwując teraz',
        'Economisește până la 25% dacă rezervi acum',
        'Ahorra hasta un 25% si reservas ahora',
      ),
      cta: BOOK,
    },
  },
  // ---------------------------------------------------------------- fitness
  {
    id: 'gym',
    category: 'fitness',
    layout: 'split',
    width: 1080,
    height: 1080,
    image: 'fitness',
    palette: P.noir,
    name: t(
      'Gym membership',
      'Абонемент у спортзал',
      'Абонемент в спортзал',
      'Karnet na siłownię',
      'Abonament la sală',
      'Membresía de gimnasio',
    ),
    copy: {
      kicker: t(
        'New year, new you',
        'Новий рік — нові цілі',
        'Новый год — новые цели',
        'Nowy rok, nowy Ty',
        'An nou, obiective noi',
        'Año nuevo, nuevo tú',
      ),
      title: t(
        'First month free',
        'Перший місяць безкоштовно',
        'Первый месяц бесплатно',
        'Pierwszy miesiąc gratis',
        'Prima lună gratuită',
        'Primer mes gratis',
      ),
      subtitle: t(
        'All clubs, all classes, no contract',
        'Усі клуби та заняття, без контракту',
        'Все клубы и занятия, без контракта',
        'Wszystkie kluby i zajęcia, bez umowy',
        'Toate cluburile și clasele, fără contract',
        'Todos los clubes y clases, sin permanencia',
      ),
      cta: JOIN,
    },
  },
  {
    id: 'fitness-story',
    category: 'fitness',
    layout: 'card',
    width: 1080,
    height: 1920,
    image: 'yoga',
    palette: P.sky,
    name: t(
      'Workout story',
      'Тренування — сторіс',
      'Тренировка — сторис',
      'Trening — relacja',
      'Antrenament — story',
      'Entrenamiento — historia',
    ),
    copy: {
      kicker: t(
        'Every Saturday',
        'Щосуботи',
        'Каждую субботу',
        'W każdą sobotę',
        'În fiecare sâmbătă',
        'Cada sábado',
      ),
      title: t(
        'Outdoor training in the park',
        'Тренування в парку',
        'Тренировка в парке',
        'Trening w parku',
        'Antrenament în parc',
        'Entrenamiento en el parque',
      ),
      subtitle: t(
        'All levels welcome',
        'Для будь-якого рівня',
        'Для любого уровня',
        'Dla każdego poziomu',
        'Pentru orice nivel',
        'Para todos los niveles',
      ),
      cta: JOIN,
    },
  },
  // ---------------------------------------------------------------- holidays
  {
    id: 'gift-card',
    category: 'holiday',
    layout: 'split',
    width: 1080,
    height: 1080,
    image: 'gift',
    palette: P.red,
    name: t(
      'Gift card',
      'Подарунковий сертифікат',
      'Подарочный сертификат',
      'Karta podarunkowa',
      'Card cadou',
      'Tarjeta regalo',
    ),
    copy: {
      kicker: t(
        'The perfect gift',
        'Ідеальний подарунок',
        'Идеальный подарок',
        'Idealny prezent',
        'Cadoul perfect',
        'El regalo perfecto',
      ),
      title: t(
        'Gift cards',
        'Подарункові сертифікати',
        'Подарочные сертификаты',
        'Karty podarunkowe',
        'Carduri cadou',
        'Tarjetas regalo',
      ),
      subtitle: t(
        'Any amount, delivered instantly by email',
        'Будь-яка сума, миттєво на email',
        'Любая сумма, мгновенно на email',
        'Dowolna kwota, od razu na e-mail',
        'Orice sumă, instant pe e-mail',
        'Cualquier importe, al instante por email',
      ),
      price: t('from $10', 'від $10', 'от $10', 'od $10', 'de la $10', 'desde $10'),
    },
  },
  {
    id: 'holiday-greeting',
    category: 'holiday',
    layout: 'hero',
    width: 1080,
    height: 1350,
    image: 'christmas-tree',
    palette: P.forest,
    name: t(
      'Holiday greeting',
      'Святкове привітання',
      'Праздничное поздравление',
      'Życzenia świąteczne',
      'Felicitare de sărbători',
      'Felicitación de fiestas',
    ),
    copy: {
      kicker: t(
        'Season’s greetings',
        'Зі святами',
        'С праздниками',
        'Czas świąt',
        'Sărbători fericite',
        'Felices fiestas',
      ),
      title: t(
        'Happy holidays from our team',
        'Щасливих свят від нашої команди',
        'Счастливых праздников от нашей команды',
        'Wesołych Świąt od naszego zespołu',
        'Sărbători fericite de la echipa noastră',
        'Felices fiestas de parte del equipo',
      ),
      subtitle: t(
        'Thank you for being with us this year',
        'Дякуємо, що були з нами цього року',
        'Спасибо, что были с нами в этом году',
        'Dziękujemy, że byliście z nami w tym roku',
        'Mulțumim că ai fost alături de noi anul acesta',
        'Gracias por acompañarnos este año',
      ),
    },
  },
  // ---------------------------------------------------------------- web ads
  {
    id: 'leaderboard',
    category: 'ads',
    layout: 'strip',
    width: 728,
    height: 90,
    image: 'sneaker',
    palette: P.indigo,
    name: t(
      'Leaderboard 728×90',
      'Лідерборд 728×90',
      'Лидерборд 728×90',
      'Leaderboard 728×90',
      'Leaderboard 728×90',
      'Leaderboard 728×90',
    ),
    copy: {
      brand: t('RUNNER', 'RUNNER', 'RUNNER', 'RUNNER', 'RUNNER', 'RUNNER'),
      title: t(
        'Sneakers up to 40% off',
        'Кросівки до −40%',
        'Кроссовки до −40%',
        'Sneakersy do −40%',
        'Sneakers până la −40%',
        'Zapatillas hasta −40%',
      ),
      subtitle: t(
        'Free delivery from $50',
        'Безкоштовна доставка від $50',
        'Бесплатная доставка от $50',
        'Darmowa dostawa od $50',
        'Livrare gratuită de la $50',
        'Envío gratis desde $50',
      ),
      cta: SHOP,
    },
  },
  {
    id: 'billboard',
    category: 'ads',
    layout: 'strip',
    width: 970,
    height: 250,
    image: 'headphones',
    palette: P.violet,
    name: t(
      'Billboard 970×250',
      'Білборд 970×250',
      'Билборд 970×250',
      'Billboard 970×250',
      'Billboard 970×250',
      'Billboard 970×250',
    ),
    copy: {
      brand: t('SOUND', 'SOUND', 'SOUND', 'SOUND', 'SOUND', 'SOUND'),
      title: t(
        'Studio Pro — sound without limits',
        'Studio Pro — звук без обмежень',
        'Studio Pro — звук без ограничений',
        'Studio Pro — dźwięk bez ograniczeń',
        'Studio Pro — sunet fără limite',
        'Studio Pro — sonido sin límites',
      ),
      subtitle: t(
        '40 hours of battery · noise cancelling',
        '40 годин роботи · шумозаглушення',
        '40 часов работы · шумоподавление',
        '40 godzin pracy · redukcja szumów',
        '40 de ore autonomie · anulare zgomot',
        '40 horas de batería · cancelación de ruido',
      ),
      cta: SHOP,
    },
  },
  {
    id: 'medium-rectangle',
    category: 'ads',
    layout: 'card',
    width: 300,
    height: 250,
    image: 'mountain-lake',
    palette: P.ocean,
    name: t(
      'Medium rectangle 300×250',
      'Прямокутник 300×250',
      'Прямоугольник 300×250',
      'Prostokąt 300×250',
      'Dreptunghi 300×250',
      'Rectángulo 300×250',
    ),
    copy: {
      title: t(
        'Mountains from $590',
        'Гори від $590',
        'Горы от $590',
        'Góry od $590',
        'Munți de la $590',
        'Montaña desde $590',
      ),
      cta: BOOK,
    },
  },
  {
    id: 'half-page',
    category: 'ads',
    layout: 'card',
    width: 300,
    height: 600,
    image: 'coffee-beans',
    palette: P.coffee,
    name: t(
      'Half page 300×600',
      'Півсторінки 300×600',
      'Полстраницы 300×600',
      'Pół strony 300×600',
      'Jumătate de pagină 300×600',
      'Media página 300×600',
    ),
    copy: {
      kicker: t(
        'Coffee club',
        'Кавовий клуб',
        'Кофейный клуб',
        'Klub kawowy',
        'Clubul cafelei',
        'Club del café',
      ),
      title: t(
        'Every 6th cup is free',
        'Кожна 6-та чашка — безкоштовно',
        'Каждая 6-я чашка — бесплатно',
        'Co 6. kawa gratis',
        'Fiecare a 6-a ceașcă e gratis',
        'Cada 6.ª taza, gratis',
      ),
      cta: JOIN,
    },
  },
  // ---------------------------------------------------------------- sport
  ...(['square', 'story', 'wide'] as const).map((fmt): GalleryEntry => ({
    id: `match-day-${fmt}`,
    category: 'sport',
    layout: 'match',
    width: fmt === 'wide' ? 1920 : 1080,
    height: fmt === 'story' ? 1920 : 1080,
    images:
      fmt === 'story'
        ? ['stadium', 'crest-green', 'crest-black']
        : fmt === 'wide'
          ? ['stadium', 'crest-red', 'crest-black']
          : ['stadium', 'crest-red', 'crest-blue'],
    palette: P.stadium,
    name: t('Match day', 'День матчу', 'День матча', 'Dzień meczu', 'Zi de meci', 'Día de partido'),
    copy: {
      kicker: t(
        'Champions Cup · Semi-final',
        'Кубок чемпіонів · Півфінал',
        'Кубок чемпионов · Полуфинал',
        'Puchar Mistrzów · Półfinał',
        'Cupa Campionilor · Semifinală',
        'Copa de Campeones · Semifinal',
      ),
      badge: t('Live', 'Наживо', 'Прямой эфир', 'Na żywo', 'Live', 'En directo'),
      home: t('North City', 'Норт Сіті', 'Норт Сити', 'North City', 'North City', 'North City'),
      away: t(
        'River United',
        'Рівер Юнайтед',
        'Ривер Юнайтед',
        'River United',
        'River United',
        'River United',
      ),
      title: t(
        'Match day',
        'День матчу',
        'День матча',
        'Dzień meczu',
        'Zi de meci',
        'Día de partido',
      ),
      subtitle: t(
        'Saturday, 9 pm',
        'Субота, 21:00',
        'Суббота, 21:00',
        'Sobota, 21:00',
        'Sâmbătă, 21:00',
        'Sábado, 21:00',
      ),
      brand: t('SPORT TV', 'SPORT TV', 'SPORT TV', 'SPORT TV', 'SPORT TV', 'SPORT TV'),
      cta: t('Watch live', 'Дивитися', 'Смотреть', 'Oglądaj', 'Urmărește', 'Ver en directo'),
    },
  })),
  {
    id: 'full-time',
    category: 'sport',
    layout: 'result',
    width: 1080,
    height: 1080,
    images: ['stadium', 'crest-red', 'crest-blue'],
    palette: P.stadium,
    name: t(
      'Full time',
      'Фінальний рахунок',
      'Итоговый счёт',
      'Wynik końcowy',
      'Scor final',
      'Resultado final',
    ),
    copy: {
      kicker: t(
        'Premier League · Round 12',
        'Прем’єр-ліга · 12 тур',
        'Премьер-лига · 12 тур',
        'Ekstraklasa · 12. kolejka',
        'Superliga · Etapa 12',
        'Primera División · Jornada 12',
      ),
      badge: t(
        'Full time',
        'Фінальний свисток',
        'Финальный свисток',
        'Koniec meczu',
        'Final',
        'Final',
      ),
      home: t('North City', 'Норт Сіті', 'Норт Сити', 'North City', 'North City', 'North City'),
      away: t(
        'River United',
        'Рівер Юнайтед',
        'Ривер Юнайтед',
        'River United',
        'River United',
        'River United',
      ),
      title: t(
        'Full time',
        'Фінальний рахунок',
        'Итоговый счёт',
        'Wynik końcowy',
        'Scor final',
        'Resultado final',
      ),
      score: t('2 : 1', '2 : 1', '2 : 1', '2 : 1', '2 : 1', '2 : 1'),
      subtitle: t(
        "Carter 12', 67' · Reid 54'",
        "Коваленко 12', 67' · Мельник 54'",
        "Иванов 12', 67' · Попов 54'",
        "Nowak 12', 67' · Wójcik 54'",
        "Popescu 12', 67' · Stan 54'",
        "García 12', 67' · Ruiz 54'",
      ),
      brand: t('SPORT TV', 'SPORT TV', 'SPORT TV', 'SPORT TV', 'SPORT TV', 'SPORT TV'),
    },
  },
  {
    id: 'game-night',
    category: 'sport',
    layout: 'match',
    width: 1080,
    height: 1350,
    images: ['arena', 'crest-black', 'crest-blue'],
    palette: P.arena,
    name: t(
      'Game night',
      'Баскетбольний вечір',
      'Баскетбольный вечер',
      'Wieczór koszykówki',
      'Seară de baschet',
      'Noche de baloncesto',
    ),
    copy: {
      kicker: t(
        'Basketball League',
        'Баскетбольна ліга',
        'Баскетбольная лига',
        'Liga koszykówki',
        'Liga de baschet',
        'Liga de baloncesto',
      ),
      badge: t(
        'Game night',
        'Ігровий вечір',
        'Игровой вечер',
        'Wieczór meczowy',
        'Seară de meci',
        'Noche de partido',
      ),
      home: t(
        'Iron Wolves',
        'Залізні Вовки',
        'Стальные Волки',
        'Żelazne Wilki',
        'Lupii de Fier',
        'Lobos de Hierro',
      ),
      away: t(
        'Blue Eagles',
        'Сині Орли',
        'Синие Орлы',
        'Niebieskie Orły',
        'Vulturii Albaștri',
        'Águilas Azules',
      ),
      title: t(
        'Game night',
        'Баскетбольний вечір',
        'Баскетбольный вечер',
        'Wieczór koszykówki',
        'Seară de baschet',
        'Noche de baloncesto',
      ),
      subtitle: t(
        'Friday, 7:30 pm · Arena',
        'Пʼятниця, 19:30 · Арена',
        'Пятница, 19:30 · Арена',
        'Piątek, 19:30 · Arena',
        'Vineri, 19:30 · Arena',
        'Viernes, 19:30 · Arena',
      ),
      cta: t('Get tickets', 'Квитки', 'Билеты', 'Bilety', 'Bilete', 'Entradas'),
    },
  },
  // ---------------------------------------------------------------- live events
  ...(['story', 'square'] as const).map((fmt): GalleryEntry => ({
    id: `festival-${fmt}`,
    category: 'event',
    layout: 'hero',
    width: 1080,
    height: fmt === 'story' ? 1920 : 1080,
    image: 'concert',
    palette: P.stage,
    name: t(
      'Music festival',
      'Музичний фестиваль',
      'Музыкальный фестиваль',
      'Festiwal muzyczny',
      'Festival de muzică',
      'Festival de música',
    ),
    copy: {
      kicker: t(
        'Live · June 14',
        'Наживо · 14 червня',
        'Вживую · 14 июня',
        'Na żywo · 14 czerwca',
        'Live · 14 iunie',
        'En directo · 14 de junio',
      ),
      title: t(
        'Summer Sound Festival',
        'Summer Sound Festival',
        'Summer Sound Festival',
        'Summer Sound Festival',
        'Summer Sound Festival',
        'Summer Sound Festival',
      ),
      subtitle: t(
        'Three stages, twenty artists, one night',
        'Три сцени, двадцять артистів, одна ніч',
        'Три сцены, двадцать артистов, одна ночь',
        'Trzy sceny, dwudziestu artystów, jedna noc',
        'Trei scene, douăzeci de artiști, o noapte',
        'Tres escenarios, veinte artistas, una noche',
      ),
      cta: t('Get tickets', 'Квитки', 'Билеты', 'Bilety', 'Bilete', 'Entradas'),
    },
  })),
  // ---------------------------------------------------------------- photo templates (Unsplash)
  {
    id: 'budget-travel',
    category: 'travel',
    layout: 'poster',
    width: 1280,
    height: 720,
    photo: 'hiker on mountain cliff above clouds',
    palette: P.poster,
    name: t(
      'Budget travel',
      'Бюджетні подорожі',
      'Бюджетные путешествия',
      'Tanie podróże',
      'Călătorii ieftine',
      'Viajes baratos',
    ),
    copy: {
      kicker: t('Episode 28', 'Епізод 28', 'Эпизод 28', 'Odcinek 28', 'Episodul 28', 'Episodio 28'),
      title: t(
        'Budget travel',
        'Бюджетні подорожі',
        'Бюджетные путешествия',
        'Tanie podróże',
        'Călătorii ieftine',
        'Viajes baratos',
      ),
    },
  },
  {
    id: 'safari',
    category: 'travel',
    layout: 'bar',
    width: 1280,
    height: 720,
    photo: 'desert dunes camel caravan',
    palette: P.wideTrack,
    name: t('Safari', 'Сафарі', 'Сафари', 'Safari', 'Safari', 'Safari'),
    copy: {
      title: t('Safari', 'Сафарі', 'Сафари', 'Safari', 'Safari', 'Safari'),
      subtitle: t(
        'Ten days in the desert · October',
        'Десять днів у пустелі · жовтень',
        'Десять дней в пустыне · октябрь',
        'Dziesięć dni na pustyni · październik',
        'Zece zile în deșert · octombrie',
        'Diez días en el desierto · octubre',
      ),
    },
  },
  {
    id: 'weekend-morning',
    category: 'social',
    layout: 'editorial',
    width: 1080,
    height: 1350,
    photo: 'woman breakfast terrace jungle view',
    palette: P.paper,
    name: t(
      'Weekend morning',
      'Ранок вихідного',
      'Утро выходного',
      'Weekendowy poranek',
      'Dimineață de weekend',
      'Mañana de fin de semana',
    ),
    copy: {
      kicker: t(
        'Slow living',
        'Повільне життя',
        'Медленная жизнь',
        'Slow life',
        'Viață lentă',
        'Vida lenta',
      ),
      title: t(
        'Weekend morning rituals',
        'Справи вихідного ранку',
        'Дела выходного утра',
        'Weekendowe rytuały',
        'Ritualuri de weekend',
        'Rituales del fin de semana',
      ),
      subtitle: t(
        'Coffee, a book and nowhere to hurry',
        'Кава, книжка й нікуди не поспішати',
        'Кофе, книга и никуда не спешить',
        'Kawa, książka i nigdzie się nie spieszyć',
        'Cafea, o carte și fără grabă',
        'Café, un libro y sin prisas',
      ),
      brand: t('@yourbrand', '@yourbrand', '@yourbrand', '@yourbrand', '@yourbrand', '@yourbrand'),
    },
  },
  {
    id: 'perfume-launch',
    category: 'ecommerce',
    layout: 'editorial',
    width: 1920,
    height: 1080,
    photo: 'perfume bottle minimal beige shadow',
    palette: P.paper,
    name: t(
      'Fragrance launch',
      'Запуск аромату',
      'Запуск аромата',
      'Premiera zapachu',
      'Lansare parfum',
      'Lanzamiento de perfume',
    ),
    copy: {
      kicker: t(
        'Spring campaign',
        'Весняна кампанія',
        'Весенняя кампания',
        'Kampania wiosenna',
        'Campanie de primăvară',
        'Campaña de primavera',
      ),
      title: t(
        'A scent of quiet mornings',
        'Аромат тихого ранку',
        'Аромат тихого утра',
        'Zapach cichych poranków',
        'Parfumul diminețiilor liniștite',
        'El aroma de las mañanas tranquilas',
      ),
      subtitle: t(
        'New eau de parfum, 50 ml',
        'Нова парфумована вода, 50 мл',
        'Новая парфюмерная вода, 50 мл',
        'Nowa woda perfumowana, 50 ml',
        'Noua apă de parfum, 50 ml',
        'Nueva eau de parfum, 50 ml',
      ),
      brand: t(
        'Maison Lumen',
        'Maison Lumen',
        'Maison Lumen',
        'Maison Lumen',
        'Maison Lumen',
        'Maison Lumen',
      ),
      price: t('$89', '2 990 ₴', '5 990 ₽', '349 zł', '399 lei', '89 €'),
    },
  },
  {
    id: 'beauty-services',
    category: 'social',
    layout: 'storyCard',
    width: 1080,
    height: 1920,
    photo: 'woman skincare soft light beige',
    palette: P.script,
    name: t(
      'Beauty price list',
      'Прайс салону краси',
      'Прайс салона красоты',
      'Cennik salonu',
      'Listă de prețuri salon',
      'Tarifas del salón',
    ),
    copy: {
      kicker: t('Studio', 'Студія', 'Студия', 'Studio', 'Studio', 'Estudio'),
      title: t(
        'Skin care',
        'Догляд за шкірою',
        'Уход за кожей',
        'Pielęgnacja skóry',
        'Îngrijirea pielii',
        'Cuidado de la piel',
      ),
      details: rows([
        t(
          'Facial cleansing|$45',
          'Чистка обличчя|1200 ₴',
          'Чистка лица|2500 ₽',
          'Oczyszczanie twarzy|180 zł',
          'Curățare facială|200 lei',
          'Limpieza facial|45 €',
        ),
        t(
          'Peeling|$30',
          'Пілінг|800 ₴',
          'Пилинг|1800 ₽',
          'Peeling|120 zł',
          'Peeling|150 lei',
          'Peeling|30 €',
        ),
        t(
          'Massage|$55',
          'Масаж|1500 ₴',
          'Массаж|3000 ₽',
          'Masaż|220 zł',
          'Masaj|250 lei',
          'Masaje|55 €',
        ),
      ]),
      brand: t(
        '@yourstudio',
        '@yourstudio',
        '@yourstudio',
        '@yourstudio',
        '@yourstudio',
        '@yourstudio',
      ),
    },
  },
  {
    id: 'coffee-menu',
    category: 'food',
    layout: 'storyCard',
    width: 1080,
    height: 1920,
    photo: 'latte art cup dark wood cafe',
    palette: P.script,
    name: t(
      'Coffee menu',
      'Кавове меню',
      'Кофейное меню',
      'Menu kawowe',
      'Meniu de cafea',
      'Carta de cafés',
    ),
    copy: {
      kicker: t('Today', 'Сьогодні', 'Сегодня', 'Dziś', 'Azi', 'Hoy'),
      title: t(
        'What is your coffee?',
        'Яка ваша улюблена кава?',
        'Какой ваш любимый кофе?',
        'Jaka jest Twoja kawa?',
        'Care e cafeaua ta?',
        '¿Cuál es tu café?',
      ),
      details: rows([
        t(
          'Espresso|$2',
          'Еспресо|45 ₴',
          'Эспрессо|150 ₽',
          'Espresso|9 zł',
          'Espresso|10 lei',
          'Espresso|1,80 €',
        ),
        t(
          'Cappuccino|$3',
          'Капучино|65 ₴',
          'Капучино|220 ₽',
          'Cappuccino|14 zł',
          'Cappuccino|14 lei',
          'Capuchino|2,50 €',
        ),
        t(
          'Flat white|$3.5',
          'Флет вайт|75 ₴',
          'Флэт уайт|250 ₽',
          'Flat white|15 zł',
          'Flat white|16 lei',
          'Flat white|3 €',
        ),
        t('Latte|$3.5', 'Лате|70 ₴', 'Латте|240 ₽', 'Latte|15 zł', 'Latte|15 lei', 'Latte|3 €'),
      ]),
      brand: t('@yourcafe', '@yourcafe', '@yourcafe', '@yourcafe', '@yourcafe', '@yourcafe'),
    },
  },
  {
    id: 'how-to-channel',
    category: 'social',
    layout: 'stack',
    width: 1280,
    height: 720,
    photo: 'smiling woman portrait studio light',
    palette: P.punch,
    name: t(
      'How-to thumbnail',
      'Обкладинка «Як зробити»',
      'Обложка «Как сделать»',
      'Miniatura poradnika',
      'Thumbnail tutorial',
      'Miniatura tutorial',
    ),
    copy: {
      title: t(
        'How to',
        'Як створити',
        'Как создать',
        'Jak stworzyć',
        'Cum să creezi',
        'Cómo crear',
      ),
      details: rows([
        t('How to', 'Як створити', 'Как создать', 'Jak stworzyć', 'Cum să creezi', 'Cómo crear'),
        t('create', 'свій', 'свой', 'własny', 'propriul', 'tu propio'),
        t('your own', 'YouTube-', 'YouTube-', 'kanał', 'canal', 'canal'),
        t('channel', 'канал', 'канал', 'YouTube', 'YouTube', 'de YouTube'),
      ]),
    },
  },
  {
    id: 'gym-story',
    category: 'fitness',
    layout: 'stack',
    width: 1080,
    height: 1920,
    photo: 'woman training gym dramatic light',
    palette: P.punch,
    name: t(
      'Gym story',
      'Сторіс для спортзалу',
      'Сторис для спортзала',
      'Story siłowni',
      'Story sală',
      'Story de gimnasio',
    ),
    copy: {
      kicker: t(
        'New season',
        'Новий сезон',
        'Новый сезон',
        'Nowy sezon',
        'Sezon nou',
        'Nueva temporada',
      ),
      title: t('Train', 'Тренуйся', 'Тренируйся', 'Trenuj', 'Antrenează-te', 'Entrena'),
      details: rows([
        t('Train', 'Тренуйся', 'Тренируйся', 'Trenuj', 'Antrenează-te', 'Entrena'),
        t('with us', 'з нами', 'с нами', 'z nami', 'cu noi', 'con nosotros'),
        t(
          '-20% in May',
          '-20% у травні',
          '-20% в мае',
          '-20% w maju',
          '-20% în mai',
          '-20% en mayo',
        ),
      ]),
    },
  },
  {
    id: 'kitchen-before-after',
    category: 'realestate',
    layout: 'beforeAfter',
    width: 1280,
    height: 720,
    photo: 'old messy kitchen',
    photo2: 'modern bright kitchen interior',
    palette: P.poster,
    name: t(
      'Before / after',
      'До / після',
      'До / после',
      'Przed / po',
      'Înainte / după',
      'Antes / después',
    ),
    copy: {
      title: t(
        'Home transformation',
        'Ремонт кухні',
        'Ремонт кухни',
        'Metamorfoza kuchni',
        'Transformarea bucătăriei',
        'Reforma de cocina',
      ),
      details: rows([
        t('Before', 'До', 'До', 'Przed', 'Înainte', 'Antes'),
        t('After', 'Після', 'После', 'Po', 'După', 'Después'),
      ]),
    },
  },
  {
    id: 'dream-apartment',
    category: 'realestate',
    layout: 'editorial',
    width: 1080,
    height: 1080,
    photo: 'modern living room interior plants daylight',
    palette: P.sage,
    name: t(
      'Apartment for sale',
      'Продаж квартири',
      'Продажа квартиры',
      'Mieszkanie na sprzedaż',
      'Apartament de vânzare',
      'Piso en venta',
    ),
    copy: {
      kicker: t(
        'New listing',
        'Новий обʼєкт',
        'Новый объект',
        'Nowa oferta',
        'Ofertă nouă',
        'Nuevo anuncio',
      ),
      title: t(
        'Your dream apartment',
        'Квартира вашої мрії',
        'Квартира вашей мечты',
        'Mieszkanie marzeń',
        'Apartamentul visurilor',
        'El piso de tus sueños',
      ),
      subtitle: t(
        '3 rooms · 84 m² · park view',
        '3 кімнати · 84 м² · вид на парк',
        '3 комнаты · 84 м² · вид на парк',
        '3 pokoje · 84 m² · widok na park',
        '3 camere · 84 m² · vedere la parc',
        '3 habitaciones · 84 m² · vistas al parque',
      ),
      price: t('$185,000', '$185 000', '$185 000', '820 000 zł', '185 000 €', '185 000 €'),
      brand: t(
        'Realty Group',
        'Realty Group',
        'Realty Group',
        'Realty Group',
        'Realty Group',
        'Realty Group',
      ),
    },
  },
  {
    id: 'pizza-deal',
    category: 'food',
    layout: 'poster',
    width: 1080,
    height: 1080,
    photo: 'pizza top view dark table',
    palette: P.poster,
    name: t(
      'Pizza deal',
      'Акція на піцу',
      'Акция на пиццу',
      'Promocja na pizzę',
      'Ofertă pizza',
      'Oferta de pizza',
    ),
    copy: {
      kicker: t(
        'Only today',
        'Лише сьогодні',
        'Только сегодня',
        'Tylko dziś',
        'Doar azi',
        'Solo hoy',
      ),
      title: t(
        'Second pizza −50%',
        'Друга піца −50%',
        'Вторая пицца −50%',
        'Druga pizza −50%',
        'A doua pizza −50%',
        'Segunda pizza −50%',
      ),
      subtitle: t(
        'Delivery in 30 minutes or it is free',
        'Доставка за 30 хвилин або безкоштовно',
        'Доставка за 30 минут или бесплатно',
        'Dostawa w 30 minut albo gratis',
        'Livrare în 30 de minute sau gratis',
        'Entrega en 30 minutos o gratis',
      ),
    },
  },
  {
    id: 'concert-night',
    category: 'event',
    layout: 'poster',
    width: 1080,
    height: 1920,
    photo: 'concert crowd stage lights',
    palette: P.poster,
    name: t('Concert night', 'Концерт', 'Концерт', 'Koncert', 'Concert', 'Concierto'),
    copy: {
      kicker: t(
        'June 14 · Arena',
        '14 червня · Арена',
        '14 июня · Арена',
        '14 czerwca · Arena',
        '14 iunie · Arena',
        '14 de junio · Arena',
      ),
      title: t(
        'Live tonight',
        'Наживо сьогодні',
        'Вживую сегодня',
        'Na żywo dziś',
        'Live în seara asta',
        'En directo esta noche',
      ),
      subtitle: t(
        'Tickets at the box office and online',
        'Квитки в касах і онлайн',
        'Билеты в кассах и онлайн',
        'Bilety w kasach i online',
        'Bilete la casă și online',
        'Entradas en taquilla y online',
      ),
    },
  },
  {
    id: 'match-of-season',
    category: 'sport',
    layout: 'poster',
    width: 1920,
    height: 1080,
    photo: 'football stadium night floodlights crowd',
    palette: P.poster,
    name: t(
      'Match of the season',
      'Матч сезону',
      'Матч сезона',
      'Mecz sezonu',
      'Meciul sezonului',
      'Partido de la temporada',
    ),
    copy: {
      kicker: t(
        'Saturday · 9 pm',
        'Субота · 21:00',
        'Суббота · 21:00',
        'Sobota · 21:00',
        'Sâmbătă · 21:00',
        'Sábado · 21:00',
      ),
      title: t(
        'Match of the season',
        'Матч сезону',
        'Матч сезона',
        'Mecz sezonu',
        'Meciul sezonului',
        'Partido de la temporada',
      ),
      subtitle: t(
        'Live on SPORT TV',
        'Наживо на SPORT TV',
        'Прямой эфир на SPORT TV',
        'Na żywo w SPORT TV',
        'Live pe SPORT TV',
        'En directo en SPORT TV',
      ),
    },
  },
  {
    id: 'autumn-lookbook',
    category: 'ecommerce',
    layout: 'editorial',
    width: 1080,
    height: 1350,
    photo: 'fashion model autumn street coat',
    palette: P.paper,
    name: t('Lookbook', 'Лукбук', 'Лукбук', 'Lookbook', 'Lookbook', 'Lookbook'),
    copy: {
      kicker: t(
        'Autumn / Winter',
        'Осінь / Зима',
        'Осень / Зима',
        'Jesień / Zima',
        'Toamnă / Iarnă',
        'Otoño / Invierno',
      ),
      title: t(
        'The autumn lookbook',
        'Осінній лукбук',
        'Осенний лукбук',
        'Jesienny lookbook',
        'Lookbook de toamnă',
        'Lookbook de otoño',
      ),
      subtitle: t(
        'Twelve looks for cold city days',
        'Дванадцять образів для холодних днів',
        'Двенадцать образов для холодных дней',
        'Dwanaście stylizacji na chłodne dni',
        'Douăsprezece ținute pentru zile reci',
        'Doce looks para días fríos',
      ),
      brand: t(
        'Studio North',
        'Studio North',
        'Studio North',
        'Studio North',
        'Studio North',
        'Studio North',
      ),
    },
  },
  // ---------------------------------------------------------------- bundled photos
  {
    id: 'beauty-salon',
    category: 'beauty',
    layout: 'split',
    width: 1080,
    height: 1080,
    image: 'beauty-portrait',
    palette: P.rose,
    name: t(
      'Beauty salon',
      'Салон краси',
      'Салон красоты',
      'Salon urody',
      'Salon de înfrumusețare',
      'Salón de belleza',
    ),
    copy: {
      kicker: t(
        'Beauty studio',
        "Б'юті-студія",
        'Бьюти-студия',
        'Studio urody',
        'Studio de frumusețe',
        'Estudio de belleza',
      ),
      title: t(
        'Your best look starts here',
        'Ваш найкращий образ починається тут',
        'Ваш лучший образ начинается здесь',
        'Twój najlepszy wygląd zaczyna się tutaj',
        'Cea mai bună versiune a ta începe aici',
        'Tu mejor look empieza aquí',
      ),
      subtitle: t(
        'Hair, makeup and nails in one visit',
        'Волосся, макіяж і манікюр за один візит',
        'Волосы, макияж и маникюр за один визит',
        'Włosy, makijaż i paznokcie podczas jednej wizyty',
        'Păr, machiaj și unghii într-o singură vizită',
        'Peluquería, maquillaje y uñas en una visita',
      ),
      cta: BOOK,
    },
  },
  {
    id: 'lipstick-collection',
    category: 'beauty',
    layout: 'hero',
    width: 1080,
    height: 1350,
    image: 'lipstick',
    palette: P.gold,
    name: t(
      'Lipstick collection',
      'Колекція помад',
      'Коллекция помад',
      'Kolekcja szminek',
      'Colecția de rujuri',
      'Colección de labiales',
    ),
    copy: {
      kicker: t(
        'New shades',
        'Нові відтінки',
        'Новые оттенки',
        'Nowe odcienie',
        'Nuanțe noi',
        'Nuevos tonos',
      ),
      title: t(
        'Red is the new black',
        'Червоний — новий чорний',
        'Красный — новый чёрный',
        'Czerwień to nowa czerń',
        'Roșul este noul negru',
        'El rojo es el nuevo negro',
      ),
      subtitle: t(
        '12 matte shades that last all day',
        '12 матових відтінків, що тримаються весь день',
        '12 матовых оттенков, которые держатся весь день',
        '12 matowych odcieni, które trzymają się cały dzień',
        '12 nuanțe mate care rezistă toată ziua',
        '12 tonos mate que duran todo el día',
      ),
      cta: SHOP,
    },
  },
  {
    id: 'beauty-masterclass',
    category: 'beauty',
    layout: 'card',
    width: 1080,
    height: 1920,
    image: 'glam-portrait',
    palette: P.gold,
    name: t(
      'Beauty masterclass',
      "Б'юті-майстер-клас",
      'Бьюти-мастер-класс',
      'Warsztaty beauty',
      'Masterclass beauty',
      'Clase de belleza',
    ),
    copy: {
      kicker: t(
        'Masterclass',
        'Майстер-клас',
        'Мастер-класс',
        'Warsztaty',
        'Masterclass',
        'Clase magistral',
      ),
      title: t(
        'Evening makeup in 60 minutes',
        'Вечірній макіяж за 60 хвилин',
        'Вечерний макияж за 60 минут',
        'Makijaż wieczorowy w 60 minut',
        'Machiaj de seară în 60 de minute',
        'Maquillaje de noche en 60 minutos',
      ),
      subtitle: t(
        'Saturday, 12:00 · Seats are limited',
        'Субота, 12:00 · Кількість місць обмежена',
        'Суббота, 12:00 · Количество мест ограничено',
        'Sobota, 12:00 · Liczba miejsc ograniczona',
        'Sâmbătă, 12:00 · Locuri limitate',
        'Sábado, 12:00 · Plazas limitadas',
      ),
      cta: JOIN,
    },
  },
  {
    id: 'car-sale',
    category: 'auto',
    layout: 'split',
    width: 1080,
    height: 1080,
    image: 'yellow-car',
    palette: P.noir,
    name: t(
      'Car for sale',
      'Авто на продаж',
      'Авто на продажу',
      'Samochód na sprzedaż',
      'Mașină de vânzare',
      'Coche en venta',
    ),
    copy: {
      kicker: t(
        'Classic collection',
        'Класична колекція',
        'Классическая коллекция',
        'Kolekcja klasyków',
        'Colecție clasică',
        'Colección clásica',
      ),
      title: t(
        'Drive a legend',
        'Керуй легендою',
        'Управляй легендой',
        'Poprowadź legendę',
        'Condu o legendă',
        'Conduce una leyenda',
      ),
      subtitle: t(
        'Restored 1970 coupé, ready for the road',
        'Відреставроване купе 1970 року, готове до дороги',
        'Отреставрированное купе 1970 года, готово к дороге',
        'Odrestaurowane coupé z 1970 roku, gotowe do drogi',
        'Coupé din 1970 restaurat, gata de drum',
        'Coupé de 1970 restaurado, listo para la carretera',
      ),
      price: t('$48,900', '$48,900', '$48,900', '$48,900', '$48,900', '$48,900'),
    },
  },
  {
    id: 'taxi-app',
    category: 'auto',
    layout: 'hero',
    width: 1200,
    height: 628,
    image: 'taxi',
    palette: P.noir,
    name: t(
      'Taxi app',
      'Додаток таксі',
      'Приложение такси',
      'Aplikacja taxi',
      'Aplicație de taxi',
      'App de taxi',
    ),
    copy: {
      kicker: t(
        'First ride −50%',
        'Перша поїздка −50%',
        'Первая поездка −50%',
        'Pierwszy kurs −50%',
        'Prima cursă −50%',
        'Primer viaje −50%',
      ),
      title: t(
        'A taxi in 3 minutes',
        'Таксі за 3 хвилини',
        'Такси за 3 минуты',
        'Taksówka w 3 minuty',
        'Taxi în 3 minute',
        'Un taxi en 3 minutos',
      ),
      subtitle: t(
        'Fixed prices, no surprises at the end of the ride',
        'Фіксовані ціни без сюрпризів наприкінці поїздки',
        'Фиксированные цены без сюрпризов в конце поездки',
        'Stałe ceny, bez niespodzianek na koniec kursu',
        'Prețuri fixe, fără surprize la final',
        'Precios fijos, sin sorpresas al final',
      ),
      cta: t('Download the app', 'Завантажити', 'Скачать', 'Pobierz', 'Descarcă', 'Descargar'),
    },
  },
  {
    id: 'car-rental',
    category: 'auto',
    layout: 'card',
    width: 1080,
    height: 1920,
    image: 'suv',
    palette: P.ocean,
    name: t(
      'Car rental',
      'Оренда авто',
      'Аренда авто',
      'Wynajem aut',
      'Închirieri auto',
      'Alquiler de coches',
    ),
    copy: {
      kicker: t(
        'Road trip season',
        'Сезон подорожей',
        'Сезон путешествий',
        'Sezon na podróże',
        'Sezonul călătoriilor',
        'Temporada de viajes',
      ),
      title: t(
        'SUV for the weekend',
        'Позашляховик на вихідні',
        'Внедорожник на выходные',
        'SUV na weekend',
        'SUV pentru weekend',
        'Un SUV para el fin de semana',
      ),
      subtitle: t(
        'Unlimited mileage and a free child seat',
        'Без обмеження пробігу, дитяче крісло безкоштовно',
        'Без ограничения пробега, детское кресло бесплатно',
        'Bez limitu kilometrów, fotelik dziecięcy gratis',
        'Kilometraj nelimitat, scaun de copil gratuit',
        'Kilometraje ilimitado y silla infantil gratis',
      ),
      price: t('$59/day', '$59/день', '$59/день', '$59/dzień', '$59/zi', '$59/día'),
      cta: BOOK,
    },
  },
  {
    id: 'vr-launch',
    category: 'tech',
    layout: 'hero',
    width: 1080,
    height: 1080,
    image: 'vr-headset',
    palette: P.violet,
    name: t(
      'Product launch',
      'Запуск продукту',
      'Запуск продукта',
      'Premiera produktu',
      'Lansare de produs',
      'Lanzamiento de producto',
    ),
    copy: {
      kicker: t('Coming soon', 'Незабаром', 'Скоро', 'Już wkrótce', 'În curând', 'Muy pronto'),
      title: t(
        'Step into a new reality',
        'Увійди в нову реальність',
        'Войди в новую реальность',
        'Wejdź w nową rzeczywistość',
        'Pășește într-o nouă realitate',
        'Entra en una nueva realidad',
      ),
      subtitle: t(
        'Pre-order now and get the game pack free',
        'Оформіть передзамовлення та отримайте ігри в подарунок',
        'Оформите предзаказ и получите игры в подарок',
        'Zamów przedpremierowo i odbierz pakiet gier gratis',
        'Precomandă acum și primești pachetul de jocuri gratuit',
        'Resérvalo ahora y llévate el pack de juegos gratis',
      ),
      cta: t('Pre-order', 'Передзамовити', 'Предзаказ', 'Zamów', 'Precomandă', 'Reservar'),
    },
  },
  {
    id: 'investing-app',
    category: 'tech',
    layout: 'split',
    width: 1200,
    height: 628,
    image: 'trading-screen',
    palette: P.ocean,
    name: t(
      'Investing app',
      'Додаток для інвестицій',
      'Приложение для инвестиций',
      'Aplikacja inwestycyjna',
      'Aplicație de investiții',
      'App de inversión',
    ),
    copy: {
      kicker: t('Fintech', 'Fintech', 'Fintech', 'Fintech', 'Fintech', 'Fintech'),
      title: t(
        'Invest from $1',
        'Інвестуйте від $1',
        'Инвестируйте от $1',
        'Inwestuj od 1 $',
        'Investește de la 1 $',
        'Invierte desde 1 $',
      ),
      subtitle: t(
        'Stocks, ETFs and gold in one app',
        'Акції, ETF і золото в одному додатку',
        'Акции, ETF и золото в одном приложении',
        'Akcje, ETF i złoto w jednej aplikacji',
        'Acțiuni, ETF-uri și aur într-o aplicație',
        'Acciones, ETF y oro en una sola app',
      ),
      cta: t('Get started', 'Почати', 'Начать', 'Zacznij', 'Începe', 'Empezar'),
    },
  },
  {
    id: 'pet-shop',
    category: 'pets',
    layout: 'split',
    width: 1080,
    height: 1080,
    image: 'happy-dog',
    palette: P.sunny,
    name: t(
      'Pet shop',
      'Зоомагазин',
      'Зоомагазин',
      'Sklep zoologiczny',
      'Pet shop',
      'Tienda de mascotas',
    ),
    copy: {
      kicker: t(
        'Pet shop',
        'Зоомагазин',
        'Зоомагазин',
        'Sklep zoologiczny',
        'Pet shop',
        'Tienda de mascotas',
      ),
      title: t(
        'Everything for happy pets',
        'Усе для щасливих улюбленців',
        'Всё для счастливых питомцев',
        'Wszystko dla szczęśliwych pupili',
        'Totul pentru animale fericite',
        'Todo para mascotas felices',
      ),
      subtitle: t(
        'Food, toys and care, delivered today',
        'Корм, іграшки та догляд з доставкою сьогодні',
        'Корм, игрушки и уход с доставкой сегодня',
        'Karma, zabawki i pielęgnacja z dostawą dziś',
        'Hrană, jucării și îngrijire cu livrare azi',
        'Comida, juguetes y cuidado con entrega hoy',
      ),
      badge: t('-20%', '-20%', '-20%', '-20%', '-20%', '-20%'),
      price: t('$14.90', '$14.90', '$14.90', '$14.90', '$14.90', '$14.90'),
    },
  },
  {
    id: 'pet-adoption',
    category: 'pets',
    layout: 'card',
    width: 1080,
    height: 1920,
    image: 'puppy',
    palette: P.mint,
    name: t(
      'Pet adoption',
      'Прилаштування тварин',
      'Пристройство животных',
      'Adopcja zwierząt',
      'Adopție animale',
      'Adopción de mascotas',
    ),
    copy: {
      kicker: t(
        "Adopt, don't shop",
        'Візьми з притулку',
        'Возьми из приюта',
        'Adoptuj, nie kupuj',
        'Adoptă, nu cumpăra',
        'Adopta, no compres',
      ),
      title: t(
        'Biscuit is looking for a home',
        'Бісквіт шукає дім',
        'Бисквит ищет дом',
        'Biszkopt szuka domu',
        'Biscuit își caută o casă',
        'Galleta busca un hogar',
      ),
      subtitle: t(
        '3 months old, vaccinated, loves cuddles',
        '3 місяці, щеплений, обожнює обійми',
        '3 месяца, привит, обожает обнимашки',
        '3 miesiące, zaszczepiony, uwielbia się przytulać',
        '3 luni, vaccinat, adoră îmbrățișările',
        '3 meses, vacunado, le encantan los mimos',
      ),
      cta: t('Meet him', 'Познайомитися', 'Познакомиться', 'Poznaj go', 'Cunoaște-l', 'Conócelo'),
    },
  },
  {
    id: 'vet-clinic',
    category: 'pets',
    layout: 'split',
    width: 1200,
    height: 628,
    image: 'cat',
    palette: P.sky,
    name: t(
      'Vet clinic',
      'Ветклініка',
      'Ветклиника',
      'Klinika weterynaryjna',
      'Clinică veterinară',
      'Clínica veterinaria',
    ),
    copy: {
      kicker: t('24/7', '24/7', '24/7', '24/7', '24/7', '24/7'),
      title: t(
        'The care your pet deserves',
        'Турбота, на яку заслуговує ваш улюбленець',
        'Забота, которую заслуживает ваш питомец',
        'Opieka, na jaką zasługuje Twój pupil',
        'Grija pe care o merită animalul tău',
        'El cuidado que tu mascota merece',
      ),
      subtitle: t(
        'Check-up and vaccination from $25',
        'Огляд і щеплення від $25',
        'Осмотр и прививка от $25',
        'Badanie i szczepienie od 25 $',
        'Control și vaccinare de la 25 $',
        'Revisión y vacunación desde 25 $',
      ),
      cta: BOOK,
    },
  },
  {
    id: 'new-year-party',
    category: 'holiday',
    layout: 'hero',
    width: 1080,
    height: 1080,
    image: 'fireworks',
    palette: P.noir,
    name: t(
      'New Year party',
      'Новорічна вечірка',
      'Новогодняя вечеринка',
      'Impreza sylwestrowa',
      'Petrecere de Revelion',
      'Fiesta de Año Nuevo',
    ),
    copy: {
      kicker: t(
        'December 31 · 10 PM',
        '31 грудня · 22:00',
        '31 декабря · 22:00',
        '31 grudnia · 22:00',
        '31 decembrie · 22:00',
        '31 de diciembre · 22:00',
      ),
      title: t(
        "Let's welcome the New Year together",
        'Зустріньмо Новий рік разом',
        'Встретим Новый год вместе',
        'Powitajmy razem Nowy Rok',
        'Să întâmpinăm împreună Noul An',
        'Recibamos juntos el Año Nuevo',
      ),
      subtitle: t(
        'Live music, fireworks and a rooftop view',
        'Жива музика, феєрверк і вид з даху',
        'Живая музыка, фейерверк и вид с крыши',
        'Muzyka na żywo, fajerwerki i widok z dachu',
        'Muzică live, artificii și priveliște de pe acoperiș',
        'Música en directo, fuegos artificiales y vistas desde la azotea',
      ),
      cta: t(
        'Get tickets',
        'Купити квиток',
        'Купить билет',
        'Kup bilet',
        'Cumpără bilet',
        'Comprar entradas',
      ),
    },
  },
  {
    id: 'valentines-day',
    category: 'holiday',
    layout: 'split',
    width: 1080,
    height: 1350,
    image: 'candy-hearts',
    palette: P.rose,
    name: t(
      "Valentine's Day",
      'День закоханих',
      'День влюблённых',
      'Walentynki',
      'Ziua Îndrăgostiților',
      'San Valentín',
    ),
    copy: {
      kicker: t(
        'February 14',
        '14 лютого',
        '14 февраля',
        '14 lutego',
        '14 februarie',
        '14 de febrero',
      ),
      title: t(
        'Sweet gifts for your sweetheart',
        'Солодкі подарунки для коханих',
        'Сладкие подарки для любимых',
        'Słodkie prezenty dla ukochanych',
        'Cadouri dulci pentru cei dragi',
        'Regalos dulces para tu amor',
      ),
      subtitle: t(
        'Handmade sweets in a gift box',
        'Цукерки ручної роботи в подарунковій коробці',
        'Конфеты ручной работы в подарочной коробке',
        'Ręcznie robione słodycze w ozdobnym pudełku',
        'Dulciuri făcute manual în cutie de cadou',
        'Dulces artesanales en caja de regalo',
      ),
      cta: SHOP,
    },
  },
  {
    id: 'autumn-sale',
    category: 'sale',
    layout: 'card',
    width: 1080,
    height: 1350,
    image: 'pumpkins',
    palette: P.coffee,
    name: t(
      'Autumn sale',
      'Осінній розпродаж',
      'Осенняя распродажа',
      'Jesienna wyprzedaż',
      'Reduceri de toamnă',
      'Rebajas de otoño',
    ),
    copy: {
      kicker: t(
        'Harvest week',
        'Тиждень урожаю',
        'Неделя урожая',
        'Tydzień zbiorów',
        'Săptămâna recoltei',
        'Semana de la cosecha',
      ),
      title: t(
        'Cozy autumn deals',
        'Затишні осінні знижки',
        'Уютные осенние скидки',
        'Przytulne jesienne okazje',
        'Oferte calde de toamnă',
        'Ofertas otoñales',
      ),
      subtitle: t(
        'Up to 30% off home decor and candles',
        'Знижки до 30% на декор і свічки',
        'Скидки до 30% на декор и свечи',
        'Do 30% taniej na dekoracje i świece',
        'Până la 30% reducere la decor și lumânări',
        'Hasta un 30% en decoración y velas',
      ),
      cta: SHOP,
    },
  },
  {
    id: 'birthday-invite',
    category: 'holiday',
    layout: 'hero',
    width: 1080,
    height: 1350,
    image: 'birthday-cake',
    palette: P.sunset,
    name: t(
      'Birthday invitation',
      'Запрошення на день народження',
      'Приглашение на день рождения',
      'Zaproszenie na urodziny',
      'Invitație de ziua de naștere',
      'Invitación de cumpleaños',
    ),
    copy: {
      kicker: t(
        'You are invited',
        'Запрошуємо',
        'Приглашаем',
        'Zapraszamy',
        'Ești invitat',
        'Estás invitado',
      ),
      title: t(
        'Anna turns 30!',
        'Анні 30!',
        'Анне 30!',
        'Anna kończy 30 lat!',
        'Ana împlinește 30 de ani!',
        '¡Ana cumple 30!',
      ),
      subtitle: t(
        'Saturday, June 14, 6 PM · Café Aurora',
        'Субота, 14 червня, 18:00 · кафе «Аврора»',
        'Суббота, 14 июня, 18:00 · кафе «Аврора»',
        'Sobota, 14 czerwca, 18:00 · kawiarnia Aurora',
        'Sâmbătă, 14 iunie, 18:00 · Cafeneaua Aurora',
        'Sábado 14 de junio, 18:00 · Café Aurora',
      ),
      cta: t('RSVP', 'Підтвердити', 'Подтвердить', 'Potwierdź', 'Confirmă', 'Confirmar'),
    },
  },
  {
    id: 'christmas-sale',
    category: 'holiday',
    layout: 'split',
    width: 1200,
    height: 628,
    image: 'christmas-ball',
    palette: P.red,
    name: t(
      'Christmas sale',
      'Різдвяний розпродаж',
      'Рождественская распродажа',
      'Świąteczna wyprzedaż',
      'Reduceri de Crăciun',
      'Rebajas de Navidad',
    ),
    copy: {
      kicker: t('Christmas', 'Різдво', 'Рождество', 'Boże Narodzenie', 'Crăciun', 'Navidad'),
      title: t(
        'Gifts for everyone you love',
        'Подарунки для всіх, кого ви любите',
        'Подарки для всех, кого вы любите',
        'Prezenty dla wszystkich, których kochasz',
        'Cadouri pentru toți cei dragi',
        'Regalos para todos los que quieres',
      ),
      subtitle: t(
        'Free gift wrapping until December 24',
        'Безкоштовне пакування до 24 грудня',
        'Бесплатная упаковка до 24 декабря',
        'Darmowe pakowanie do 24 grudnia',
        'Ambalare gratuită până pe 24 decembrie',
        'Envoltorio gratis hasta el 24 de diciembre',
      ),
      badge: t('-25%', '-25%', '-25%', '-25%', '-25%', '-25%'),
      cta: SHOP,
    },
  },
  {
    id: 'live-concert',
    category: 'event',
    layout: 'hero',
    width: 1080,
    height: 1350,
    image: 'guitarist',
    palette: P.violet,
    name: t(
      'Live concert',
      'Живий концерт',
      'Живой концерт',
      'Koncert na żywo',
      'Concert live',
      'Concierto en directo',
    ),
    copy: {
      kicker: t(
        'Friday · 8 PM',
        "П'ятниця · 20:00",
        'Пятница · 20:00',
        'Piątek · 20:00',
        'Vineri · 20:00',
        'Viernes · 20:00',
      ),
      title: t(
        'Acoustic evening under the stars',
        'Акустичний вечір просто неба',
        'Акустический вечер под открытым небом',
        'Akustyczny wieczór pod gwiazdami',
        'Seară acustică sub cerul liber',
        'Noche acústica bajo las estrellas',
      ),
      subtitle: t(
        'Blues, folk and your favourite songs',
        'Блюз, фолк і улюблені пісні',
        'Блюз, фолк и любимые песни',
        'Blues, folk i ulubione piosenki',
        'Blues, folk și cântecele preferate',
        'Blues, folk y tus canciones favoritas',
      ),
      cta: t(
        'Get tickets',
        'Купити квиток',
        'Купить билет',
        'Kup bilet',
        'Cumpără bilet',
        'Comprar entradas',
      ),
    },
  },
  {
    id: 'wedding-invite',
    category: 'event',
    layout: 'card',
    width: 1080,
    height: 1350,
    image: 'wedding',
    palette: P.cream,
    name: t(
      'Wedding invitation',
      'Весільне запрошення',
      'Свадебное приглашение',
      'Zaproszenie ślubne',
      'Invitație de nuntă',
      'Invitación de boda',
    ),
    copy: {
      kicker: t(
        'Save the date',
        'Збережіть дату',
        'Сохраните дату',
        'Zarezerwuj datę',
        'Rezervă data',
        'Reserva la fecha',
      ),
      title: t(
        'Emma & Leo are getting married',
        'Емма і Лео одружуються',
        'Эмма и Лео женятся',
        'Emma i Leo biorą ślub',
        'Emma și Leo se căsătoresc',
        'Emma y Leo se casan',
      ),
      subtitle: t(
        'August 23 · Villa Rosa, Lake Garda',
        '23 серпня · Вілла Роза, озеро Гарда',
        '23 августа · Вилла Роза, озеро Гарда',
        '23 sierpnia · Villa Rosa, jezioro Garda',
        '23 august · Villa Rosa, Lacul Garda',
        '23 de agosto · Villa Rosa, lago de Garda',
      ),
    },
  },
  {
    id: 'tennis-club',
    category: 'fitness',
    layout: 'split',
    width: 1200,
    height: 628,
    image: 'tennis',
    palette: P.cream,
    name: t(
      'Tennis club',
      'Тенісний клуб',
      'Теннисный клуб',
      'Klub tenisowy',
      'Club de tenis',
      'Club de tenis',
    ),
    copy: {
      kicker: t(
        'Summer camp',
        'Літній табір',
        'Летний лагерь',
        'Obóz letni',
        'Tabără de vară',
        'Campamento de verano',
      ),
      title: t(
        'Tennis for kids and adults',
        'Теніс для дітей і дорослих',
        'Теннис для детей и взрослых',
        'Tenis dla dzieci i dorosłych',
        'Tenis pentru copii și adulți',
        'Tenis para niños y adultos',
      ),
      subtitle: t(
        'First lesson free · All levels',
        'Перше заняття безкоштовно · Будь-який рівень',
        'Первое занятие бесплатно · Любой уровень',
        'Pierwsza lekcja gratis · Każdy poziom',
        'Prima lecție gratuită · Orice nivel',
        'Primera clase gratis · Todos los niveles',
      ),
      cta: JOIN,
    },
  },
  {
    id: 'surf-school',
    category: 'fitness',
    layout: 'hero',
    width: 1080,
    height: 1920,
    image: 'surfing',
    palette: P.ocean,
    name: t(
      'Surf school',
      'Школа серфінгу',
      'Школа серфинга',
      'Szkoła surfingu',
      'Școală de surf',
      'Escuela de surf',
    ),
    copy: {
      kicker: t(
        'Bali · 7 days',
        'Балі · 7 днів',
        'Бали · 7 дней',
        'Bali · 7 dni',
        'Bali · 7 zile',
        'Bali · 7 días',
      ),
      title: t(
        'Catch your first wave',
        'Злови свою першу хвилю',
        'Поймай свою первую волну',
        'Złap swoją pierwszą falę',
        'Prinde primul tău val',
        'Coge tu primera ola',
      ),
      subtitle: t(
        'Camp with coaches, a board and a villa by the ocean',
        'Табір з тренерами, дошкою та віллою біля океану',
        'Лагерь с тренерами, доской и виллой у океана',
        'Obóz z trenerami, deską i willą nad oceanem',
        'Tabără cu antrenori, placă și vilă la ocean',
        'Campamento con monitores, tabla y villa junto al océano',
      ),
      cta: BOOK,
    },
  },
  {
    id: 'bakery',
    category: 'food',
    layout: 'card',
    width: 1080,
    height: 1080,
    image: 'bakery',
    palette: P.cream,
    name: t('Bakery', 'Пекарня', 'Пекарня', 'Piekarnia', 'Brutărie', 'Panadería'),
    copy: {
      kicker: t(
        'Baked this morning',
        'Спечено сьогодні вранці',
        'Испечено сегодня утром',
        'Upieczone dziś rano',
        'Copt în această dimineață',
        'Recién horneado',
      ),
      title: t(
        'Warm buns at 8 AM',
        'Теплі булочки о 8:00',
        'Тёплые булочки в 8:00',
        'Ciepłe bułeczki o 8:00',
        'Chifle calde la 8:00',
        'Bollos calientes a las 8:00',
      ),
      price: t('$1.50', '$1.50', '$1.50', '$1.50', '$1.50', '$1.50'),
      cta: t('Order now', 'Замовити', 'Заказать', 'Zamów', 'Comandă', 'Pedir ahora'),
    },
  },
  {
    id: 'smoothie-bar',
    category: 'food',
    layout: 'split',
    width: 1080,
    height: 1350,
    image: 'smoothie',
    palette: P.rose,
    name: t(
      'Smoothie bar',
      'Смузі-бар',
      'Смузи-бар',
      'Smoothie bar',
      'Smoothie bar',
      'Bar de batidos',
    ),
    copy: {
      kicker: t(
        'New on the menu',
        'Новинка в меню',
        'Новинка в меню',
        'Nowość w menu',
        'Nou în meniu',
        'Nuevo en la carta',
      ),
      title: t(
        'Berry boost smoothie',
        'Ягідний смузі',
        'Ягодный смузи',
        'Smoothie jagodowe',
        'Smoothie de fructe de pădure',
        'Batido de frutos rojos',
      ),
      subtitle: t(
        'Raspberry, banana, oat milk and mint',
        "Малина, банан, вівсяне молоко та м'ята",
        'Малина, банан, овсяное молоко и мята',
        'Malina, banan, mleko owsiane i mięta',
        'Zmeură, banană, lapte de ovăz și mentă',
        'Frambuesa, plátano, leche de avena y menta',
      ),
      badge: t('2+1', '2+1', '2+1', '2+1', '2+1', '2+1'),
      price: t('$4.90', '$4.90', '$4.90', '$4.90', '$4.90', '$4.90'),
    },
  },
  {
    id: 'sea-view-apartments',
    category: 'realestate',
    layout: 'split',
    width: 1200,
    height: 628,
    image: 'apartments',
    palette: P.sky,
    name: t(
      'New apartments',
      'Нові квартири',
      'Новые квартиры',
      'Nowe mieszkania',
      'Apartamente noi',
      'Pisos nuevos',
    ),
    copy: {
      kicker: t(
        'Move in this year',
        'Заселення цього року',
        'Заселение в этом году',
        'Wprowadzenie w tym roku',
        'Mutare anul acesta',
        'Entrega este año',
      ),
      title: t(
        'Apartments with a sea view',
        'Квартири з видом на море',
        'Квартиры с видом на море',
        'Mieszkania z widokiem na morze',
        'Apartamente cu vedere la mare',
        'Pisos con vistas al mar',
      ),
      subtitle: t(
        '0% installment plan for 24 months',
        'Розстрочка 0% на 24 місяці',
        'Рассрочка 0% на 24 месяца',
        'Raty 0% na 24 miesiące',
        'Rate 0% pe 24 de luni',
        'Financiación al 0% en 24 meses',
      ),
      cta: MORE,
    },
  },
  {
    id: 'city-break',
    category: 'travel',
    layout: 'hero',
    width: 1080,
    height: 1920,
    image: 'venice',
    palette: P.sunset,
    name: t(
      'City break',
      'Подорож містом',
      'Городской уикенд',
      'City break',
      'City break',
      'Escapada urbana',
    ),
    copy: {
      kicker: t(
        'Weekend in Venice',
        'Вихідні у Венеції',
        'Выходные в Венеции',
        'Weekend w Wenecji',
        'Weekend la Veneția',
        'Fin de semana en Venecia',
      ),
      title: t(
        'Fall in love with Italy',
        'Закохайтеся в Італію',
        'Влюбитесь в Италию',
        'Zakochaj się we Włoszech',
        'Îndrăgostește-te de Italia',
        'Enamórate de Italia',
      ),
      subtitle: t(
        'Flights and hotel from $390',
        'Переліт і готель від $390',
        'Перелёт и отель от $390',
        'Lot i hotel od 390 $',
        'Zbor și hotel de la 390 $',
        'Vuelo y hotel desde 390 $',
      ),
      cta: BOOK,
    },
  },
  {
    id: 'greek-islands',
    category: 'travel',
    layout: 'card',
    width: 1080,
    height: 1350,
    image: 'santorini',
    palette: P.sky,
    name: t(
      'Greek islands',
      'Грецькі острови',
      'Греческие острова',
      'Greckie wyspy',
      'Insulele grecești',
      'Islas griegas',
    ),
    copy: {
      kicker: t(
        'Last minute',
        'Гарячий тур',
        'Горящий тур',
        'Last minute',
        'Last minute',
        'Última hora',
      ),
      title: t(
        'Santorini, 7 nights',
        'Санторіні, 7 ночей',
        'Санторини, 7 ночей',
        'Santorini, 7 nocy',
        'Santorini, 7 nopți',
        'Santorini, 7 noches',
      ),
      subtitle: t(
        'Hotel with breakfast and a sea view',
        'Готель зі сніданками та видом на море',
        'Отель с завтраками и видом на море',
        'Hotel ze śniadaniem i widokiem na morze',
        'Hotel cu mic-dejun și vedere la mare',
        'Hotel con desayuno y vistas al mar',
      ),
      price: t('$790', '$790', '$790', '$790', '$790', '$790'),
      cta: BOOK,
    },
  },
  {
    id: 'giveaway',
    category: 'social',
    layout: 'card',
    width: 1080,
    height: 1350,
    image: 'fashion-selfie',
    palette: P.sunset,
    name: t('Giveaway', 'Розіграш', 'Розыгрыш', 'Konkurs', 'Giveaway', 'Sorteo'),
    copy: {
      kicker: t('Giveaway', 'Розіграш', 'Розыгрыш', 'Konkurs', 'Giveaway', 'Sorteo'),
      title: t(
        'Win a weekend shopping spree',
        'Виграй шопінг на вихідні',
        'Выиграй шопинг на выходные',
        'Wygraj weekendowe zakupy',
        'Câștigă o sesiune de shopping',
        'Gana un fin de semana de compras',
      ),
      subtitle: t(
        'Follow, like and tag a friend',
        'Підпишись, постав лайк і відміть друга',
        'Подпишись, поставь лайк и отметь друга',
        'Obserwuj, polub i oznacz znajomego',
        'Urmărește, apreciază și etichetează un prieten',
        'Sigue, dale like y etiqueta a un amigo',
      ),
    },
  },
  {
    id: 'lookbook',
    category: 'ecommerce',
    layout: 'split',
    width: 1080,
    height: 1350,
    image: 'fashion-model',
    palette: P.gold,
    name: t('Lookbook', 'Лукбук', 'Лукбук', 'Lookbook', 'Lookbook', 'Lookbook'),
    copy: {
      kicker: t(
        'Spring / Summer',
        'Весна / Літо',
        'Весна / Лето',
        'Wiosna / Lato',
        'Primăvară / Vară',
        'Primavera / Verano',
      ),
      title: t(
        'Effortless white',
        'Легкість білого',
        'Лёгкость белого',
        'Lekkość bieli',
        'Eleganța albului',
        'Blanco sin esfuerzo',
      ),
      subtitle: t(
        'Linen and cotton for warm days',
        'Льон і бавовна для теплих днів',
        'Лён и хлопок для тёплых дней',
        'Len i bawełna na ciepłe dni',
        'In și bumbac pentru zilele calde',
        'Lino y algodón para días cálidos',
      ),
      cta: SHOP,
    },
  },
  {
    id: 'leather-shoes',
    category: 'ecommerce',
    layout: 'split',
    width: 1200,
    height: 628,
    image: 'leather-shoes',
    palette: P.cream,
    name: t(
      'Leather shoes',
      'Шкіряне взуття',
      'Кожаная обувь',
      'Skórzane buty',
      'Pantofi din piele',
      'Zapatos de piel',
    ),
    copy: {
      kicker: t(
        'Handmade',
        'Ручна робота',
        'Ручная работа',
        'Ręcznie robione',
        'Lucrat manual',
        'Hecho a mano',
      ),
      title: t(
        'Classic oxfords',
        'Класичні оксфорди',
        'Классические оксфорды',
        'Klasyczne oksfordy',
        'Oxford clasici',
        'Oxford clásicos',
      ),
      subtitle: t(
        'Italian leather, lifetime repairs',
        'Італійська шкіра, ремонт довічно',
        'Итальянская кожа, ремонт пожизненно',
        'Włoska skóra, dożywotnie naprawy',
        'Piele italiană, reparații pe viață',
        'Piel italiana, reparaciones de por vida',
      ),
      price: t('$189', '$189', '$189', '$189', '$189', '$189'),
    },
  },
  {
    id: 'family-testimonial',
    category: 'social',
    layout: 'quote',
    width: 1080,
    height: 1080,
    image: 'family',
    palette: P.white,
    name: t(
      'Family review',
      'Відгук родини',
      'Отзыв семьи',
      'Opinia rodziny',
      'Recenzia unei familii',
      'Opinión de una familia',
    ),
    copy: {
      title: t(
        'We found a place where the whole family feels at home.',
        'Ми знайшли місце, де вся родина почувається як удома.',
        'Мы нашли место, где вся семья чувствует себя как дома.',
        'Znaleźliśmy miejsce, w którym cała rodzina czuje się jak w domu.',
        'Am găsit un loc unde toată familia se simte ca acasă.',
        'Encontramos un lugar donde toda la familia se siente en casa.',
      ),
      author: t(
        'The Millers',
        'Родина Мельників',
        'Семья Мельниковых',
        'Rodzina Millerów',
        'Familia Miller',
        'Familia Miller',
      ),
      role: t(
        'Members since 2021',
        'Клієнти з 2021 року',
        'Клиенты с 2021 года',
        'Klienci od 2021 roku',
        'Clienți din 2021',
        'Clientes desde 2021',
      ),
    },
  },
  {
    id: 'remote-jobs',
    category: 'hiring',
    layout: 'split',
    width: 1080,
    height: 1080,
    image: 'remote-work',
    palette: P.mint,
    name: t(
      'Remote jobs',
      'Віддалена робота',
      'Удалённая работа',
      'Praca zdalna',
      'Joburi remote',
      'Empleo remoto',
    ),
    copy: {
      kicker: t(
        "We're hiring",
        'Ми наймаємо',
        'Мы нанимаем',
        'Rekrutujemy',
        'Angajăm',
        'Contratamos',
      ),
      title: t(
        'Work from anywhere',
        'Працюй звідусіль',
        'Работай откуда угодно',
        'Pracuj z dowolnego miejsca',
        'Lucrează de oriunde',
        'Trabaja desde cualquier lugar',
      ),
      subtitle: t(
        'Support specialists, flexible hours, paid courses',
        'Фахівці підтримки, гнучкий графік, оплачене навчання',
        'Специалисты поддержки, гибкий график, оплачиваемое обучение',
        'Specjaliści wsparcia, elastyczne godziny, płatne kursy',
        'Specialiști suport, program flexibil, cursuri plătite',
        'Especialistas de soporte, horario flexible, cursos pagados',
      ),
      cta: t('Apply', 'Відгукнутися', 'Откликнуться', 'Aplikuj', 'Aplică', 'Postular'),
    },
  },
];

function localize(copy: LocalCopy, locale: GalleryLocale): Copy {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(copy))
    if (v) out[k] = (v as Record<GalleryLocale, unknown>)[locale];
  return out as unknown as Copy;
}

/** Build the document of a gallery template in the given language. */
export function buildGalleryDoc(entry: GalleryEntry, locale: GalleryLocale): TemplateDoc {
  return LAYOUTS[entry.layout]({
    width: entry.width,
    height: entry.height,
    palette: entry.palette,
    copy: localize(entry.copy, locale),
    image: entry.image,
    images: entry.images,
    photo: entry.photo,
    photo2: entry.photo2,
  });
}

export function findGalleryEntry(id: string): GalleryEntry | undefined {
  return GALLERY.find((e) => e.id === id);
}
